import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { sendNotificationEmail, notifyUnionStaff } from '../utils/sendNotificationEmail';
import ZoomableImg from './ZoomableImg';
import VocationWorksLogo from './VocationWorksLogo';
import LineIcon from './LineIcon';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// Object keys ('30'/'45'/'60') are the stable values stored in interviews.duration_minutes
// and used as the modal <select>'s option values -- only the display text is translated,
// via messaging.duration30/45/60 (resolved by DURATION_KEY_MAP below, since these object
// keys can't themselves be i18n keys).
const DURATION_LABELS = { '30': '30 minutes', '45': '45 minutes', '60': '1 hour' };
const DURATION_KEY_MAP = { '30': 'messaging.duration30', '45': 'messaging.duration45', '60': 'messaging.duration60' };
// Meeting platform names (Google Meet, Zoom, Microsoft Teams) are third-party brand names
// and "Vocation Works Video" is our own product name -- brand names are never translated,
// consistent with how "Vocation" itself is left untranslated elsewhere in the app.
// Transcript re-check while 'pending' (see the effect that uses these).
const TRANSCRIPT_POLL_INTERVAL_MS = 60 * 1000;
const TRANSCRIPT_POLL_MAX_MS      = 20 * 60 * 1000;

const MEETING_TYPE_LABELS = { google_meet: 'Google Meet', zoom: 'Zoom', teams: 'Microsoft Teams', daily: 'Vocation Works Video' };

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'png', 'jpg', 'jpeg', 'gif', 'zip'];
const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_EXTENSIONS.map(e => `.${e}`).join(',');

function AttachmentIcon({ type }) {
  return type?.startsWith('image/') ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <circle cx="8.5" cy="8.5" r="1.5"></circle>
      <polyline points="21 15 16 10 5 21"></polyline>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
    </svg>
  );
}

const channelMap = {};
const pendingUnreadConvIds = new Set();

// Which conversations column holds "my side" and my own hide flag, per userType. Organization
// threads (conversation_type 'worker_organization') carry only worker_id + organization_id.
const SIDE_COL   = { worker: 'worker_id', employer: 'employer_id', union: 'union_id', organization: 'organization_id' };
const HIDDEN_COL = { worker: 'worker_hidden_at', employer: 'employer_hidden_at', union: 'union_hidden_at', organization: 'organization_hidden_at' };

// A new message resurfaces the thread for whichever side had hidden it. Worker-employer and
// worker-union rows keep clearing the same three columns as always; organization rows clear
// only their own two (worker + organization).
function unhidePatch(conv, userType) {
  const base = { updated_at: new Date().toISOString() };
  return conv?.organization_id || userType === 'organization'
    ? { ...base, worker_hidden_at: null, organization_hidden_at: null }
    : { ...base, worker_hidden_at: null, employer_hidden_at: null, union_hidden_at: null };
}

// The other party's id (their inbox channel / notification target).
function counterpartId(conv, userType) {
  return userType === 'worker' ? (conv.employer_id || conv.union_id || conv.organization_id) : conv.worker_id;
}

// A message should only be marked read when the recipient has actually seen it -- having
// the conversation "open" isn't enough, since that stays true even while the tab is
// backgrounded/unfocused (switched away, minimized, other app in front). Without this check,
// read_at gets set the instant a broadcast arrives for the active conversation, regardless of
// whether anyone is actually looking at the screen, which defeats unread tracking entirely.
function isTabActive() {
  return document.visibilityState === 'visible' && document.hasFocus();
}

async function getOrCreateChannel(supabase, channelName) {
  if (!channelMap[channelName]) {
    const channel = supabase.channel(channelName, {
      config: { broadcast: { self: true, ack: false } },
    });
    channelMap[channelName] = { channel, status: 'SUBSCRIBING' };
    channel.subscribe((status) => {
      channelMap[channelName].status = status;
    });
  }

  // Wait for SUBSCRIBED status (max 3 seconds)
  const start = Date.now();
  while (channelMap[channelName].status !== 'SUBSCRIBED' && Date.now() - start < 3000) {
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  return channelMap[channelName].channel;
}

// Channels in channelMap are shared/persistent across component mounts (there's no
// channel.off() in this supabase-js version to unbind a specific listener on unmount).
// Binding the raw `.on()` listener more than once per channel+event would stack a new
// handler on every remount without ever removing the old one. Instead, bind exactly
// once per channel+event and route through a swappable pointer that each mount
// overwrites with its own live callback -- so only the current instance ever runs.
function bindBroadcastOnce(channelName, channel, event, handler) {
  const entry = channelMap[channelName];
  if (!entry.handlers) entry.handlers = {};
  if (!entry.boundEvents) entry.boundEvents = new Set();
  entry.handlers[event] = handler;
  if (entry.boundEvents.has(event)) return;
  entry.boundEvents.add(event);
  channel.on('broadcast', { event }, (msg) => entry.handlers[event]?.(msg));
}

function formatTime12h(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 6; h <= 22; h++) {
    const minutes = h === 22 ? [0] : [0, 15, 30, 45];
    for (const m of minutes) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      opts.push({ value, label: formatTime12h(value) });
    }
  }
  return opts;
})();

// Module-level helper (not a component) -- uses the raw i18next singleton rather than
// the useTranslation hook, since it has no render cycle of its own. It's always called
// fresh from within Messaging's render, so it still picks up language switches.
function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return i18n.t('messaging.now');
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return i18n.t('messaging.today');
  if (date.toDateString() === yesterday.toDateString()) return i18n.t('messaging.yesterday');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function parseInterviewContent(content) {
  try {
    const raw = content.slice('INTERVIEW:'.length);
    const parsed = JSON.parse(raw);
    // Guard against non-object results
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

// Job offer messages: "OFFER:" + JSON { offer_id, application_id, job_title, pay, start_date },
// posted into the thread by the employer when an offer is sent (EmployerDashboard handleSendOffer).
export const OFFER_PREFIX = 'OFFER:';
function parseOfferContent(content) {
  try {
    const parsed = JSON.parse(content.slice(OFFER_PREFIX.length));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

// Posts a message into a conversation from outside an open Messaging view (the employer's offer
// modal) and pings both sides' realtime channels, the same way a typed message does.
export async function postConversationMessage(supabase, { conversationId, senderId, senderType, otherPartyId, content }) {
  const { data: newMsg, error } = await supabase
    .from('messages')
    .insert({ conversation_id: conversationId, sender_id: senderId, sender_type: senderType, content })
    .select()
    .single();
  if (error || !newMsg) return null;
  await supabase.from('conversations')
    .update({ updated_at: new Date().toISOString(), worker_hidden_at: null, employer_hidden_at: null, union_hidden_at: null })
    .eq('id', conversationId);
  (await getOrCreateChannel(supabase, `conversation:${conversationId}`)).send({ type: 'broadcast', event: 'new_message', payload: { message: newMsg } });
  if (otherPartyId) {
    (await getOrCreateChannel(supabase, `inbox:${otherPartyId}`)).send({ type: 'broadcast', event: 'new_message_notification', payload: { conversation_id: conversationId, message: newMsg } });
  }
  return newMsg;
}

// If notes look like a copy-pasted meeting invite (contains newlines), extract just the first URL.
// Otherwise return notes as-is for pre-wrap display.
function notesDisplay(notes) {
  if (!notes) return '';
  if (!notes.includes('\n')) return notes;
  const urlMatch = notes.match(/https?:\/\/[^\s]+/);
  return urlMatch ? urlMatch[0] : notes;
}

function InterviewCard({ details, isMine, onJoinVideo }) {
  const { t } = useTranslation();
  const isDaily = details.meetingType === 'daily' || details.meetingLink === 'daily';

  const formattedDate = (() => {
    try {
      return new Date(details.date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
      });
    } catch {
      return details.date;
    }
  })();

  return (
    <div style={{
      maxWidth: '68%',
      borderLeft: `4px solid ${AMBER}`,
      backgroundColor: isMine ? '#1A3A5C' : 'rgba(255,255,255,0.08)',
      borderRadius: '0 12px 12px 0',
      padding: '12px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontFamily: FONT_HEAD, fontSize: '1.15rem', color: AMBER, letterSpacing: '0.04em' }}>
        <LineIcon name="calendar" />
        {t('messaging.interviewScheduledLabel')}
      </div>
      <div style={{ fontSize: '13px', color: WHITE, lineHeight: 1.5 }}>
        <strong>{t('messaging.dateLabel')}</strong> {formattedDate}
      </div>
      <div style={{ fontSize: '13px', color: WHITE, lineHeight: 1.5 }}>
        <strong>{t('messaging.timeLabel')}</strong> {formatTime12h(details.time)}
      </div>
      <div style={{ fontSize: '13px', color: WHITE, lineHeight: 1.5 }}>
        <strong>{t('messaging.durationLabel')}</strong> {DURATION_KEY_MAP[details.duration] ? t(DURATION_KEY_MAP[details.duration]) : (DURATION_LABELS[details.duration] || details.duration)}
      </div>
      {isDaily ? (
        <div style={{ marginTop: '4px' }}>
          <button
            onClick={onJoinVideo}
            style={{
              backgroundColor: AMBER,
              color: NAVY,
              border: 'none', borderRadius: '20px',
              padding: '6px 14px', fontSize: '12px', fontWeight: 700,
              fontFamily: FONT_BODY,
              cursor: 'pointer',
            }}
          >
            {t('messaging.joinVideoCall')}
          </button>
        </div>
      ) : (
        <div style={{ fontSize: '13px', color: WHITE, lineHeight: 1.5 }}>
          <strong>{t('messaging.meetingLinkLabel')}</strong>{' '}
          <a
            href={details.meetingLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: AMBER, textDecoration: 'underline' }}
          >
            {t('messaging.joinMeeting')}
          </a>
        </div>
      )}
      {details.notes && (
        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
          <strong>{t('messaging.notesLabel')}</strong> {notesDisplay(details.notes)}
        </div>
      )}
    </div>
  );
}

// Offer card in the thread: role, pay and start date. The worker gets "View offer" (opens the
// offer in their Applications tab via onViewOffer); the employer side reads "Offer sent".
function OfferCard({ details, isMine, userType, onViewOffer }) {
  const { t } = useTranslation();
  const forWorker = userType === 'worker';
  const startDate = (() => {
    if (!details.start_date) return null;
    try {
      return new Date(details.start_date + 'T00:00:00').toLocaleDateString(i18n.language?.startsWith('es') ? 'es-US' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return details.start_date; }
  })();
  const row = (label, value) => value ? (
    <div style={{ fontSize: '14px', color: WHITE, lineHeight: 1.5 }}><strong>{label}</strong> {value}</div>
  ) : null;
  return (
    <div data-offer-card style={{
      borderLeft: '4px solid #8B5CF6', backgroundColor: isMine ? '#1A3A5C' : 'rgba(255,255,255,0.08)',
      borderRadius: '0 12px 12px 0', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0,
    }}>
      <div style={{ fontFamily: FONT_HEAD, fontSize: '1.15rem', color: '#B794F6', letterSpacing: '0.04em' }}>
        {forWorker ? t('messaging.offerCardTitle') : t('messaging.offerSentTitle')}
      </div>
      {row(t('messaging.offerRoleLabel'), details.job_title)}
      {row(t('messaging.offerPayLabel'), details.pay)}
      {row(t('messaging.offerStartLabel'), startDate)}
      {forWorker && onViewOffer && details.application_id && (
        <button
          type="button"
          onClick={() => onViewOffer(details.application_id)}
          style={{
            marginTop: '4px', alignSelf: 'flex-start', minHeight: '44px', padding: '0 18px',
            backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '22px',
            fontSize: '14px', fontWeight: 700, fontFamily: FONT_BODY, cursor: 'pointer',
          }}
        >
          {t('messaging.viewOffer')}
        </button>
      )}
    </div>
  );
}

// The Messages badge unit: conversations with at least one unread message from the other side
// (the same unit the conversation list marks as unread), not individual messages. Used by
// Messaging itself and by the dashboards' initial badge count.
export async function countUnreadConversations(supabase, conversationIds, side) {
  if (!conversationIds?.length) return 0;
  const { data } = await supabase
    .from('messages')
    .select('conversation_id')
    .in('conversation_id', conversationIds)
    // sender_type identifies which SIDE of the conversation a message came from -- reliable
    // regardless of which individual teammate sent it, unlike sender_id.
    .neq('sender_type', side)
    .is('read_at', null);
  return new Set((data || []).map(m => m.conversation_id)).size;
}

export default function Messaging({ supabase, userId, userType, actingUserId, initialConversationId, senderName, onSendOffer, onViewOffer, onUnreadCountChange, onMobileThreadChange, emptyListText, support }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // The real logged-in individual, distinct from `userId` -- for worker/union callers
  // (no multi-seat concept) this is just their own id, same as userId. For employer
  // callers, userId stays the shared company id (needed for conversations.employer_id /
  // is_employer_staff relationship scoping); actingUserId is the actual staff member.
  const myId = actingUserId || userId;
  // employer_id -> Map(user_id -> { display_name, avatar_url }), for attributing which
  // specific staff member sent an employer-side message/scheduled an interview. Only
  // ever populated for employer conversations; worker/union have no such ambiguity.
  const [employerStaffMap, setEmployerStaffMap] = useState({});
  // union_id -> { user_id -> union_staff row }. Only loadable from the union side:
  // union_staff's SELECT policy is is_union_staff(), so a worker falls back to the union's
  // shared name/logo -- the same fallback employer attribution already uses.
  const [unionStaffMap, setUnionStaffMap] = useState({});
  // organization_id -> { user_id -> organization_staff row }, same idea as unionStaffMap:
  // teammate-facing attribution on the organization side only.
  const [orgStaffMap, setOrgStaffMap] = useState({});
  // Organization side: the open thread's worker is no longer a current member, so RLS has
  // taken the thread away (or a send was refused). Shown instead of the composer.
  const [threadGone, setThreadGone] = useState(false);
  // Friendly pre-check only, mirroring the same employer_plan_usage RPC and
  // has_active_subscription pattern already used to gate job-posting and staff invites --
  // the real, unbypassable enforcement is the messages INSERT RLS policy itself. Starts
  // `true` (fails open) so a not-yet-loaded check never flashes a false block; workers and
  // unions never fetch this at all; a canceled/never-subscribed employer's own attempt would
  // be rejected by RLS regardless of this value.
  const [employerSubscriptionActive, setEmployerSubscriptionActive] = useState(true);
  const messagingBlocked = userType === 'employer' && !employerSubscriptionActive;
  const [conversations, setConversations]   = useState([]);
  const [activeId, setActiveId]             = useState(initialConversationId || null);
  const [messages, setMessages]             = useState([]);
  const [text, setText]                     = useState('');
  const [sending, setSending]               = useState(false);
  const [attachUploading, setAttachUploading] = useState(false);
  const [downloadingId, setDownloadingId]     = useState(null);
  const [isDraggingFile, setIsDraggingFile]   = useState(false);
  const attachInputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const [isComposing, setIsComposing]       = useState(false);
  const [loadingConvs, setLoadingConvs]     = useState(true);
  const [loadingMsgs, setLoadingMsgs]       = useState(false);
  const [isMobile, setIsMobile]             = useState(window.innerWidth < 640);
  const [mobileView, setMobileView]         = useState(initialConversationId ? 'thread' : 'list');
  const [hiredApplication, setHiredApplication] = useState(null);
  const [lastMessages, setLastMessages]              = useState({});
  const [conversationListVersion, setConversationListVersion] = useState(0);

  // Message delete / conversation hide state
  const [msgActionId, setMsgActionId]               = useState(null);
  const [msgConfirmDeleteId, setMsgConfirmDeleteId] = useState(null);
  const [convMenuOpenId, setConvMenuOpenId]         = useState(null);
  const [convConfirmDeleteId, setConvConfirmDeleteId] = useState(null);
  const conversationIdsRef = useRef(new Set());

  // Interview scheduling state
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [interviewForm, setInterviewForm] = useState({ date: '', time: '', duration: '30', meetingType: 'daily', meetingLink: '', notes: '' });
  const [sendingInterview, setSendingInterview] = useState(false);
  const [activeInterview, setActiveInterview] = useState(null);
  // Interview rescheduling state -- rescheduleTarget non-null puts the Schedule Interview
  // modal into "edit this existing interview" mode instead of creating a new one. Only the
  // original interviewer (employer/union) gets that direct power; the worker can only submit
  // a reschedule REQUEST with a reason, tracked via requestChangeTarget below.
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [requestChangeTarget, setRequestChangeTarget] = useState(null);
  const [requestChangeReason, setRequestChangeReason] = useState('');
  const [submittingRequestChange, setSubmittingRequestChange] = useState(false);
  const [decliningRequestId, setDecliningRequestId] = useState(null);

  const messagesEndRef   = useRef(null);
  const textareaRef      = useRef(null);
  const channelRef       = useRef(null);
  const activeConvIdRef  = useRef(activeId);
  const lastActiveConvRef = useRef(null);

  // Track latest callbacks in refs to avoid stale closures in realtime handler
  const loadConvsRef  = useRef(null);
  const markAsReadRef = useRef(null);

  useEffect(() => { activeConvIdRef.current = activeId; }, [activeId]);
  useEffect(() => { conversationIdsRef.current = new Set(conversations.map(c => c.id)); }, [conversations]);

  // Catch-up: a message that arrived in the active conversation while this tab was
  // backgrounded/unfocused is deliberately left unread (see the broadcast handler below).
  // Once the tab actually becomes visible and focused again, mark it read now -- this is
  // the moment the recipient genuinely sees it, not whenever the broadcast happened to land.
  useEffect(() => {
    const markActiveConvReadIfVisible = () => {
      if (isTabActive() && activeConvIdRef.current) markAsReadRef.current(activeConvIdRef.current);
    };
    document.addEventListener('visibilitychange', markActiveConvReadIfVisible);
    window.addEventListener('focus', markActiveConvReadIfVisible);
    return () => {
      document.removeEventListener('visibilitychange', markActiveConvReadIfVisible);
      window.removeEventListener('focus', markActiveConvReadIfVisible);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Tells the host dashboard when a phone-width thread is open, so it can give the chat the
  // whole screen above its bottom nav (no footer links or banners).
  const mobileThreadOpen = isMobile && mobileView === 'thread' && !!activeId;
  useEffect(() => {
    onMobileThreadChange?.(mobileThreadOpen);
  }, [mobileThreadOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onMobileThreadChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close open conversation menu / message action icon on outside click
  useEffect(() => {
    const closeMenus = () => { setConvMenuOpenId(null); setMsgActionId(null); };
    document.addEventListener('click', closeMenus);
    return () => document.removeEventListener('click', closeMenus);
  }, []);

  // ── Real unread count (DB-backed) ───────────────────────────
  const getRealUnreadCount = async () => {
    const hiddenCol = HIDDEN_COL[userType] || 'employer_hidden_at';
    let convQuery = supabase.from('conversations').select('id').is(hiddenCol, null);
    if (SIDE_COL[userType]) convQuery = convQuery.eq(SIDE_COL[userType], userId);

    const { data: convs } = await convQuery;
    if (!convs || convs.length === 0) return 0;

    return countUnreadConversations(supabase, convs.map(c => c.id), userType);
  };

  useEffect(() => {
    if (!userId) return;
    const loadInitialUnread = async () => {
      const realCount = await getRealUnreadCount();
      onUnreadCountChange?.(realCount, true); // true = absolute value, not delta
    };
    loadInitialUnread();
  }, [userId]); // eslint-disable-line

  // ── Load conversations ──────────────────────────────────────
  const loadConversations = async () => {
    const col = SIDE_COL[userType] || 'employer_id';
    const hiddenCol = HIDDEN_COL[userType] || 'employer_hidden_at';
    const { data: convs } = await supabase
      .from('conversations')
      .select('id, worker_id, employer_id, union_id, organization_id, job_id, updated_at')
      .eq(col, userId)
      .is(hiddenCol, null)
      .order('updated_at', { ascending: false });

    if (!convs || convs.length === 0) {
      setConversations([]);
      setLoadingConvs(false);
      return;
    }

    const convIds     = convs.map(c => c.id);
    const workerIds   = [...new Set(convs.map(c => c.worker_id))];
    const employerIds = [...new Set(convs.map(c => c.employer_id).filter(Boolean))];
    const unionIds    = [...new Set(convs.map(c => c.union_id).filter(Boolean))];
    const orgIds      = [...new Set(convs.map(c => c.organization_id).filter(Boolean))];

    const [{ data: allMsgs }, { data: workers }, { data: employers }, { data: unions }, { data: trades }, { data: orgs }, { data: workerPhotos }] = await Promise.all([
      supabase.from('messages')
        .select('id, conversation_id, content, created_at, sender_id, sender_type, read_at, deleted_at, attachment_url, attachment_name, attachment_type')
        .in('conversation_id', convIds)
        .order('created_at', { ascending: false }),
      supabase.from('workers').select('id, first_name, last_name').in('id', workerIds),
      employerIds.length
        ? supabase.from('employers').select('id, company_name, logo_url').in('id', employerIds)
        : Promise.resolve({ data: [] }),
      unionIds.length
        ? supabase.from('union_accounts').select('id, union_name, logo_url').in('id', unionIds)
        : Promise.resolve({ data: [] }),
      userType !== 'worker'
        ? supabase.from('worker_trades').select('worker_id, trade').in('worker_id', workerIds)
        : Promise.resolve({ data: [] }),
      // Organization threads: the worker sees the organization's name + logo.
      userType === 'worker' && orgIds.length
        ? supabase.from('organizations').select('id, org_name, logo_url').in('id', orgIds)
        : Promise.resolve({ data: [] }),
      // The organization side shows the worker's photo (falls back to initials if unreadable).
      userType === 'organization'
        ? supabase.from('worker_profiles').select('id, profile_photo_url').in('id', workerIds)
        : Promise.resolve({ data: [] }),
    ]);

    const lastMsgMap = {};
    allMsgs?.forEach(m => { if (!lastMsgMap[m.conversation_id]) lastMsgMap[m.conversation_id] = m; });

    const workerMap   = Object.fromEntries((workers || []).map(w => [w.id, w]));
    const employerMap = Object.fromEntries((employers || []).map(e => [e.id, e]));
    const unionMap    = Object.fromEntries((unions || []).map(u => [u.id, u]));
    const tradeMap    = Object.fromEntries((trades || []).map(t => [t.worker_id, t.trade]));
    const orgMap      = Object.fromEntries((orgs || []).map(o => [o.id, o]));
    const photoMap    = Object.fromEntries((workerPhotos || []).map(p => [p.id, p.profile_photo_url]));

    const enriched = convs.map(c => {
      const w = workerMap[c.worker_id];
      const e = employerMap[c.employer_id];
      const u = unionMap[c.union_id];

      let otherPartyName, otherPartyInitials, otherPartyLogoUrl;
      if (userType === 'worker') {
        if (c.organization_id) {
          const o = orgMap[c.organization_id];
          otherPartyName     = o?.org_name || t('messaging.supportOrganizationLabel');
          otherPartyInitials = o?.org_name?.[0] || '?';
          otherPartyLogoUrl  = o?.logo_url || null;
        } else if (c.union_id) {
          otherPartyName     = u?.union_name || 'Union';
          otherPartyInitials = u?.union_name?.[0] || '?';
          otherPartyLogoUrl  = u?.logo_url || null;
        } else {
          otherPartyName     = e?.company_name || 'Employer';
          otherPartyInitials = e?.company_name?.[0] || '?';
          otherPartyLogoUrl  = e?.logo_url || null;
        }
      } else {
        otherPartyName     = (`${w?.first_name || ''} ${w?.last_name || ''}`).trim() || 'Worker';
        otherPartyInitials = `${w?.first_name?.[0] || ''}${w?.last_name?.[0] || ''}` || '?';
        otherPartyLogoUrl  = photoMap[c.worker_id] || null;
      }
      const otherPartyTrade = tradeMap[c.worker_id] || '';

      return { ...c, lastMessage: lastMsgMap[c.id] || null, otherPartyName, otherPartyInitials, otherPartyLogoUrl, otherPartyTrade };
    });

    const withPending = pendingUnreadConvIds.size > 0
      ? enriched.map(conv =>
          pendingUnreadConvIds.has(conv.id) && conv.id !== activeConvIdRef.current && conv.lastMessage
            ? { ...conv, lastMessage: { ...conv.lastMessage, read_at: null } }
            : conv
        )
      : enriched;
    setConversations(withPending);
    setLoadingConvs(false);
  };
  loadConvsRef.current = loadConversations;

  useEffect(() => { loadConversations(); }, [userId, userType]); // eslint-disable-line

  useEffect(() => {
    if (userType !== 'employer' || !userId) return;
    supabase.rpc('employer_plan_usage', { p_employer_id: userId }).then(({ data }) => {
      setEmployerSubscriptionActive(data?.has_active_subscription ?? true);
    });
  }, [userId, userType]); // eslint-disable-line

  // ── Load messages ──────────────────────────────────────────
  const loadMessages = async (convId) => {
    setLoadingMsgs(true);
    const { data } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', convId)
      .order('created_at', { ascending: true });
    setMessages(data || []);
    setLoadingMsgs(false);
  };

  // ── Mark messages as read ──────────────────────────────────
  const markAsRead = async (convId) => {
    const { data } = await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('conversation_id', convId)
      .neq('sender_type', userType)
      .is('read_at', null)
      .select('id, read_at');
    // The catch-up path (tab regains focus) fires this well after the message was rendered,
    // with no other trigger that would otherwise refetch the list -- without syncing local
    // state here too, the unread dot/bold would keep showing a since-cleared DB row until
    // the next full reload.
    if (data && data.length > 0) {
      const readAt = data[0].read_at;
      setConversations(prev => prev.map(c =>
        c.id === convId && c.lastMessage && c.lastMessage.sender_type !== userType
          ? { ...c, lastMessage: { ...c.lastMessage, read_at: readAt } }
          : c
      ));
      setLastMessages(prev =>
        prev[convId] && prev[convId].sender_type !== userType
          ? { ...prev, [convId]: { ...prev[convId], read_at: readAt } }
          : prev
      );
      // Recompute the true total (rather than tracking a delta) so the parent dashboard's
      // badge/stat-card count self-corrects regardless of what it already held -- e.g. it
      // may already be 0 from the "clear on tab open" reset, in which case a naive delta
      // would drift negative.
      onUnreadCountChange?.(await getRealUnreadCount(), true);
    }
  };
  markAsReadRef.current = markAsRead;

  // ── Realtime subscription (broadcast) ─────────────────────
  useEffect(() => {
    if (!activeId) return;
    loadMessages(activeId);
    // Opening a conversation via a click (or a notification deep link) is itself a genuine
    // view, so this one fires unconditionally -- it's the live broadcast case below that
    // needs the visibility/focus check, since that fires just from having the conversation
    // already open, independent of whether the tab is actually in front right now.
    if (isTabActive()) markAsRead(activeId);
    loadActiveInterview(activeId);

    const setup = async () => {
      const channelName = `conversation:${activeId}`;
      const channel = await getOrCreateChannel(supabase, channelName);
      bindBroadcastOnce(channelName, channel, 'new_message', ({ payload }) => {
        console.log('[MsgDebug] Received broadcast new_message:', payload.message);
        setMessages(prev => {
          if (prev.find(m => m.id === payload.message.id)) return prev;
          return [...prev, payload.message];
        });
        setLastMessages(prev => ({ ...prev, [activeId]: payload.message }));
        if (payload.message.sender_type !== userType && isTabActive()) markAsReadRef.current(activeId);
      });
      bindBroadcastOnce(channelName, channel, 'message_deleted', ({ payload }) => {
        setMessages(prev => prev.map(m => m.id === payload.message_id ? { ...m, deleted_at: payload.deleted_at } : m));
      });
      channelRef.current = channel;

      // Pre-subscribe to the other party's inbox so it's SUBSCRIBED before we send
      const activeConv = conversations.find(c => c.id === activeId);
      const otherPartyId = activeConv?.worker_id === userId ? (activeConv?.employer_id || activeConv?.union_id || activeConv?.organization_id) : activeConv?.worker_id;
      if (otherPartyId) {
        await getOrCreateChannel(supabase, `inbox:${otherPartyId}`);
      }
    };
    setup();
  }, [activeId]); // eslint-disable-line

  // ── Inbox subscription (conversation list level) ───────────
  useEffect(() => {
    if (!userId) return;
    const setup = async () => {
      const channelName = `inbox:${userId}`;
      const inboxChannel = await getOrCreateChannel(supabase, channelName);
      bindBroadcastOnce(channelName, inboxChannel, 'new_message_notification', ({ payload }) => {
        console.log('[MsgDebug] Received inbox notification:', payload.message);
        const isActiveConv = activeConvIdRef.current === payload.conversation_id;
        const messageForList = isActiveConv
          ? payload.message
          : { ...payload.message, read_at: null };

        if (!conversationIdsRef.current.has(payload.conversation_id)) {
          // Conversation was hidden (or not yet loaded) — un-hide it so new activity isn't missed
          const hiddenCol = HIDDEN_COL[userType] || 'employer_hidden_at';
          supabase.from('conversations').update({ [hiddenCol]: null }).eq('id', payload.conversation_id).then(() => {
            loadConvsRef.current();
          });
        }

        setLastMessages(prev => ({
          ...prev,
          [payload.conversation_id]: messageForList,
        }));
        setConversations(prev => prev.map(conv =>
          conv.id === payload.conversation_id
            ? { ...conv, lastMessage: messageForList }
            : conv
        ));
        if (!isActiveConv) {
          pendingUnreadConvIds.add(payload.conversation_id);
          setConversationListVersion(v => v + 1);
        }
        if (isActiveConv) {
          setMessages(prev => {
            if (prev.find(m => m.id === payload.message.id)) return prev;
            return [...prev, payload.message];
          });
        } else {
          // Recount rather than +1: a second message in an already-unread conversation
          // doesn't add a conversation to the badge.
          getRealUnreadCount().then(n => onUnreadCountChange?.(n, true));
        }
      });
    };
    setup();
  }, [userId]); // eslint-disable-line

  // ── Scroll to bottom ───────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages]);

  // ── Live-update the interview banner when the other party accepts/declines,
  //    or a new interview gets scheduled, without requiring a reload ─────────
  useEffect(() => {
    if (!activeId) return;
    const channel = supabase
      .channel(`interviews:conv:${activeId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'interviews', filter: `conversation_id=eq.${activeId}` },
        () => loadActiveInterview(activeId)
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [activeId]); // eslint-disable-line

  // ── Re-check a Daily interview's transcript whenever it's viewed (poll, not a webhook --
  //    see get-interview-transcript.js), regardless of interview_at: a call can be held
  //    before the scheduled time, and call-end (InterviewPage's left-meeting) isn't a
  //    reliable trigger since a closed tab never fires it. Safe on upcoming interviews --
  //    the endpoint leaves null alone until Daily actually has a transcript. While it's
  //    'pending' and the conversation stays open, keep polling every 60s for up to 20 min.
  useEffect(() => {
    const iv = activeInterview;
    if (!iv || iv.meeting_type !== 'daily' || !iv.meeting_link) return;
    if (iv.transcript_status !== null && iv.transcript_status !== 'pending') return;
    let cancelled = false;
    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled || !session?.access_token) return;
      try {
        const res = await fetch('/api/get-interview-transcript', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ interviewId: iv.id }),
        });
        if (cancelled || !res.ok) return;
        const result = await res.json();
        if (cancelled || (result.status ?? null) === iv.transcript_status) return;
        setActiveInterview(prev => (prev && prev.id === iv.id ? { ...prev, transcript_status: result.status ?? null } : prev));
      } catch { /* unchanged; next poll or next view re-checks */ }
    };
    check();
    if (iv.transcript_status !== 'pending') return () => { cancelled = true; };
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt >= TRANSCRIPT_POLL_MAX_MS) { clearInterval(timer); return; }
      check();
    }, TRANSCRIPT_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [activeInterview?.id, activeInterview?.transcript_status]); // eslint-disable-line

  // ── Check hired status for active conversation ─────────────
  useEffect(() => {
    const conv = conversations.find(c => c.id === activeId);
    if (!conv || !conv.employer_id) { setHiredApplication(null); return; }
    const check = async () => {
      const { data } = await supabase
        .from('applications')
        .select('id')
        .eq('worker_id', conv.worker_id)
        .eq('employer_id', conv.employer_id)
        .eq('status', 'hired')
        .maybeSingle();
      setHiredApplication(data || null);
    };
    check();
  }, [activeId]); // eslint-disable-line

  // ── Resolve real per-person identity for the employer side of this conversation ──
  // (worker/union have no multi-seat concept, so there's nothing to resolve for them).
  // Cached per employer_id since the same employer's roster is reused across every
  // conversation with them, not refetched per message.
  useEffect(() => {
    const conv = conversations.find(c => c.id === activeId);
    const employerId = conv?.employer_id;
    if (!employerId || employerStaffMap[employerId]) return;
    const loadStaffMap = async () => {
      const { data } = await supabase
        .from('employer_staff')
        .select('user_id, display_name, avatar_url')
        .eq('employer_id', employerId);
      const map = {};
      (data || []).forEach(row => { map[row.user_id] = row; });
      setEmployerStaffMap(prev => ({ ...prev, [employerId]: map }));
    };
    loadStaffMap();
  }, [activeId, conversations]); // eslint-disable-line

  // ── Same per-person identity for the union side, once unions have union_staff seats ──
  useEffect(() => {
    if (userType !== 'union') return;
    const conv = conversations.find(c => c.id === activeId);
    const unionId = conv?.union_id;
    if (!unionId || unionStaffMap[unionId]) return;
    const loadUnionStaffMap = async () => {
      const { data } = await supabase
        .from('union_staff')
        .select('user_id, display_name, avatar_url')
        .eq('union_id', unionId);
      const map = {};
      (data || []).forEach(row => { map[row.user_id] = row; });
      setUnionStaffMap(prev => ({ ...prev, [unionId]: map }));
    };
    loadUnionStaffMap();
  }, [activeId, conversations, userType]); // eslint-disable-line

  // ── Organization side: which staff member sent each organization message ──
  useEffect(() => {
    if (userType !== 'organization' || !userId || orgStaffMap[userId]) return;
    supabase
      .from('organization_staff')
      .select('user_id, display_name, avatar_url')
      .eq('organization_id', userId)
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(row => { map[row.user_id] = row; });
        setOrgStaffMap(prev => ({ ...prev, [userId]: map }));
      });
  }, [userType, userId]); // eslint-disable-line

  // ── Organization side: an open thread that is no longer listed (the worker disconnected,
  //    so RLS hides it) shows "no longer connected" instead of an empty, broken view ──
  useEffect(() => { setThreadGone(false); }, [activeId]);
  // Access is gone, so the history goes too; the header keeps the last known name.
  useEffect(() => { if (threadGone) setMessages([]); }, [threadGone]);
  useEffect(() => {
    if (userType !== 'organization' || !activeId || loadingConvs) return;
    if (!conversations.some(c => c.id === activeId)) setThreadGone(true);
  }, [userType, activeId, loadingConvs, conversations]);

  // ── Auto-grow textarea ─────────────────────────────────────
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 80) + 'px';
  }, [text]);

  // ── Deliver an already-inserted message: optimistic add, broadcast, inbox notify, email ──
  const deliverNewMessage = async (newMsg) => {
    if (!newMsg) return;
    setMessages(prev => {
      if (prev.find(m => m.id === newMsg.id)) return prev;
      return [...prev, newMsg];
    });
    console.log('[MsgDebug] Optimistic add:', newMsg);
    setLastMessages(prev => ({ ...prev, [activeId]: newMsg }));
    // Broadcast to receiver
    (await getOrCreateChannel(supabase, `conversation:${activeId}`)).send({
      type: 'broadcast',
      event: 'new_message',
      payload: { message: newMsg },
    });
    // Notify recipient's inbox channel
    const convForInbox = conversations.find(c => c.id === activeId);
    if (convForInbox) {
      const otherPartyId = counterpartId(convForInbox, userType);
      (await getOrCreateChannel(supabase, `inbox:${otherPartyId}`)).send({
        type: 'broadcast',
        event: 'new_message_notification',
        payload: { conversation_id: activeId, message: newMsg },
      });
    }
    const conv = conversations.find(c => c.id === activeId);
    // Worker -> organization: organization notifications are per staff login, which a worker
    // can't look up, so the organization sees it as an unread thread in Messages instead.
    if (conv && !(userType === 'worker' && conv.organization_id)) {
      const otherId   = userType === 'worker' ? (conv.employer_id || conv.union_id) : conv.worker_id;
      const otherType = userType === 'worker' ? (conv.employer_id ? 'employer' : 'union') : 'worker';
      const msgText = `${senderName || 'Someone'} sent you a message`;
      const msgLink = JSON.stringify({ tab: 'Messages', conversation_id: activeId });
      if (otherType === 'union') {
        // A union is a multi-seat account, so the message reaches every active staff member
        // rather than only the owner's id -- both in-app and by email.
        notifyUnionStaff(supabase, {
          unionId:     otherId,
          inAppType:   'new_message',
          emailType:   'new_message',
          message:     msgText,
          linkTo:      msgLink,
          sender_name: senderName || 'Someone',
        });
      } else {
        supabase.from('notifications').insert({
          user_id:   otherId,
          user_type: otherType,
          type:      'new_message',
          message:   msgText,
          link_to:   msgLink,
        }).then(() => {});
        sendNotificationEmail(supabase, {
          recipientId:   otherId,
          recipientType: otherType,
          type:          'new_message',
          sender_name:   senderName || 'Someone',
        });
      }
    }
    loadConvsRef.current();
  };

  // ── Send message ───────────────────────────────────────────
  const handleSend = async () => {
    if (!text.trim() || !activeId || sending || messagingBlocked) return;
    const content = text.trim();
    setText('');
    setSending(true);
    const { data: newMsg, error: sendError } = await supabase
      .from('messages')
      .insert({ conversation_id: activeId, sender_id: myId, sender_type: userType, content })
      .select()
      .single();
    // Organization side: a refused send almost always means the worker disconnected (RLS
    // only allows current members). Say so instead of failing silently (the text is put back
    // in case the refusal was something else and the thread is still there).
    if (userType === 'organization' && (sendError || !newMsg)) {
      console.error('[Messaging] organization send refused:', sendError);
      setText(content);
      setSending(false);
      setThreadGone(true);
      loadConvsRef.current();
      return;
    }
    // Clear hidden_at for both sides -- a new message should resurface the conversation
    // for a previously-deleted thread, for whichever side(s) had hidden it (self and/or
    // recipient). The column that doesn't apply to this conversation's type is already
    // null (e.g. union_hidden_at on a worker-employer row), so clearing all three unconditionally is safe.
    await supabase.from('conversations')
      .update(unhidePatch(conversations.find(c => c.id === activeId), userType))
      .eq('id', activeId);
    await deliverNewMessage(newMsg);
    setSending(false);
  };

  // ── Attach & send a file ────────────────────────────────────
  // Shared by both the click-to-select <input> and drag-and-drop -- takes a raw
  // File (not a DOM event) so neither caller has to duplicate validation/upload logic.
  const sendAttachmentFile = async (file) => {
    if (!file || !activeId || attachUploading || messagingBlocked) return;

    if (file.size > MAX_ATTACHMENT_BYTES) {
      alert(t('messaging.attachmentTooLarge'));
      return;
    }
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
      alert(t('messaging.attachmentTypeNotSupported'));
      return;
    }

    setAttachUploading(true);
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${activeId}/${Date.now()}_${safeName}`;
    const { error: uploadError } = await supabase.storage
      .from('message-attachments')
      .upload(path, file, { contentType: file.type });
    if (uploadError) {
      setAttachUploading(false);
      alert(t('messaging.uploadFailedPrefix') + uploadError.message);
      return;
    }

    const { data: newMsg, error: insertError } = await supabase
      .from('messages')
      .insert({
        conversation_id: activeId, sender_id: myId, sender_type: userType,
        // messages.content has a NOT NULL + non-empty CHECK constraint; an attachment-only
        // send has no user-typed text, so we use an internal marker here the same way
        // INTERVIEW: is used above -- the render logic below keys off attachment_url and
        // never displays this raw content, so it's never shown to the user.
        content: '[Attachment]', attachment_url: path, attachment_name: file.name, attachment_type: file.type,
      })
      .select()
      .single();
    if (insertError) {
      setAttachUploading(false);
      alert(t('messaging.couldNotSendAttachmentPrefix') + insertError.message);
      return;
    }

    await supabase.from('conversations')
      .update(unhidePatch(conversations.find(c => c.id === activeId), userType))
      .eq('id', activeId);
    await deliverNewMessage(newMsg);
    setAttachUploading(false);
  };

  const handleAttachmentSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    await sendAttachmentFile(file);
  };

  // ── Drag-and-drop attach ─────────────────────────────────────
  // Desktop-only in practice (native HTML5 DnD events don't fire from touch input),
  // so this is purely additive on mobile -- isDraggingFile can never become true there.
  // A ref-based counter avoids the flicker you'd get from dragleave firing every time
  // the pointer crosses a child element's boundary.
  const handleDragEnter = (e) => {
    e.preventDefault();
    if (!activeId || userType === 'organization' || conversations.find(c => c.id === activeId)?.organization_id) return;
    dragCounterRef.current += 1;
    if (e.dataTransfer.types?.includes('Files')) setIsDraggingFile(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDraggingFile(false);
    }
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDraggingFile(false);
    if (!activeId || attachUploading || userType === 'organization' || conversations.find(c => c.id === activeId)?.organization_id) return;

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    if (files.length > 1) {
      alert(t('messaging.attachmentOnlyOneAtATime'));
      return;
    }
    await sendAttachmentFile(files[0]);
  };

  // ── Download an attachment (private bucket, so always via a fresh signed URL) ──
  const handleDownloadAttachment = async (msg) => {
    if (!msg.attachment_url) return;
    setDownloadingId(msg.id);
    const { data, error } = await supabase.storage
      .from('message-attachments')
      .createSignedUrl(msg.attachment_url, 60);
    setDownloadingId(null);
    if (error || !data?.signedUrl) {
      alert(t('messaging.couldNotOpenFilePrefix') + (error?.message || t('messaging.unknownErrorLower')));
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  // ── Schedule interview (real interviews row, not a message) ─
  const handleSendInterviewInvite = async () => {
    const { date, time, duration, meetingType, meetingLink, notes } = interviewForm;
    const isDaily = meetingType === 'daily';
    if (!date || !time || (!isDaily && !meetingLink.trim()) || !activeId || messagingBlocked) return;
    setSendingInterview(true);

    const conv = conversations.find(c => c.id === activeId);
    if (!conv) { setSendingInterview(false); return; }

    const interviewId = crypto.randomUUID();
    let finalMeetingLink = isDaily ? null : meetingLink.trim();

    // Daily rooms are created eagerly here (not lazily at join time) so the room URL can be
    // persisted on the interviews row -- that's what lets the dashboard "Upcoming Interview"
    // card join the meeting directly without round-tripping through this component.
    if (isDaily) {
      try {
        const expiryTimestamp = Math.floor(new Date(`${date}T${time}:00`).getTime() / 1000) + (24 * 3600);
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch('/api/create-daily-room', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({ convId: activeId, interviewId, expiryTimestamp }),
        });
        const room = await res.json();
        if (!res.ok) {
          alert(t('messaging.couldNotCreateVideoRoomPrefix') + (room.error || t('messaging.unknownError')));
          setSendingInterview(false);
          return;
        }
        finalMeetingLink = room.url;
      } catch (err) {
        alert(t('messaging.somethingWrongCreatingRoomPrefix') + err.message);
        setSendingInterview(false);
        return;
      }
    }

    const { data: newInterview, error: ivError } = await supabase
      .from('interviews')
      .insert({
        id: interviewId,
        conversation_id: activeId,
        worker_id: conv.worker_id,
        employer_id: conv.employer_id || null,
        union_id: conv.union_id || null,
        job_id: conv.job_id || null,
        interview_at: new Date(`${date}T${time}:00`).toISOString(),
        duration_minutes: Number(duration),
        meeting_type: meetingType || 'google_meet',
        meeting_link: finalMeetingLink,
        notes: notes.trim(),
        status: 'pending',
        created_by: myId,
      })
      .select()
      .single();

    if (ivError) {
      alert(t('messaging.couldNotScheduleInterviewPrefix') + ivError.message);
      setSendingInterview(false);
      return;
    }
    setActiveInterview(newInterview);

    let formattedDate = date;
    try {
      formattedDate = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
      });
    } catch {}

    // Plain, human-readable message announcing the interview -- the interview record itself
    // lives in `interviews`, not embedded in this message's content.
    const content = `📅 Interview scheduled for ${formattedDate} at ${formatTime12h(time)}.`;
    const { data: newMsg } = await supabase
      .from('messages')
      .insert({ conversation_id: activeId, sender_id: myId, sender_type: userType, content })
      .select()
      .single();
    // Clear hidden_at for both sides -- see handleSend for why clearing all three is safe.
    await supabase.from('conversations')
      .update({ updated_at: new Date().toISOString(), worker_hidden_at: null, employer_hidden_at: null, union_hidden_at: null })
      .eq('id', activeId);
    if (newMsg) {
      setMessages(prev => {
        if (prev.find(m => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });
      setLastMessages(prev => ({ ...prev, [activeId]: newMsg }));
      (await getOrCreateChannel(supabase, `conversation:${activeId}`)).send({
        type: 'broadcast',
        event: 'new_message',
        payload: { message: newMsg },
      });
      // Notify recipient's inbox channel
      const otherPartyId = userType === 'worker' ? conv.employer_id : conv.worker_id;
      (await getOrCreateChannel(supabase, `inbox:${otherPartyId}`)).send({
        type: 'broadcast',
        event: 'new_message_notification',
        payload: { conversation_id: activeId, message: newMsg },
      });
    }

    supabase.from('notifications').insert({
      user_id:   conv.worker_id,
      user_type: 'worker',
      type:      'interview_scheduled',
      message:   `${senderName || 'An employer'} has scheduled an interview with you on ${formattedDate} at ${formatTime12h(time)}`,
      link_to:   JSON.stringify({ tab: 'Messages', conversation_id: activeId }),
    }).then(() => {});
    sendNotificationEmail(supabase, {
      recipientId:   conv.worker_id,
      recipientType: 'worker',
      type:          'interview_scheduled',
      company_name:  senderName || 'An employer',
      date:          formattedDate,
      time,
    });

    setShowInterviewModal(false);
    setInterviewForm({ date: '', time: '', duration: '30', meetingType: 'daily', meetingLink: '', notes: '' });
    setSendingInterview(false);
    loadConvsRef.current();
  };

  // ── Load the soonest upcoming (non-cancelled/completed) interview for this conversation ──
  const loadActiveInterview = async (convId) => {
    const nowIso = new Date().toISOString();
    const { data: upcoming } = await supabase
      .from('interviews')
      .select('*')
      .eq('conversation_id', convId)
      .not('status', 'in', '(cancelled,completed)')
      .gte('interview_at', nowIso)
      .order('interview_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (upcoming) { setActiveInterview(upcoming); return; }

    // No upcoming interview -- fall back to the most recent past one so its card (and any
    // transcript once ready) is still reachable after closing and reopening the conversation,
    // instead of only ever existing in memory right after scheduling it.
    const { data: past } = await supabase
      .from('interviews')
      .select('*')
      .eq('conversation_id', convId)
      .not('status', 'in', '(cancelled,completed)')
      .lt('interview_at', nowIso)
      .order('interview_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setActiveInterview(past || null);
  };

  // ── Worker accepts/declines an interview ────────────────────
  const handleInterviewResponse = async (interviewId, newStatus) => {
    const updates = { status: newStatus };
    if (newStatus === 'accepted') updates.accepted_at = new Date().toISOString();
    const { data, error } = await supabase.from('interviews').update(updates).eq('id', interviewId).select().single();
    if (error) { alert(t('messaging.couldNotUpdateInterviewPrefix') + error.message); return; }
    setActiveInterview(data);

    const conv = conversations.find(c => c.id === activeId);
    if (conv) {
      const otherId   = conv.employer_id || conv.union_id;
      const otherType = conv.employer_id ? 'employer' : 'union';
      supabase.from('notifications').insert({
        user_id:   otherId,
        user_type: otherType,
        type:      newStatus === 'accepted' ? 'interview_accepted' : 'interview_declined',
        message:   `${senderName || 'The candidate'} has ${newStatus === 'accepted' ? 'confirmed' : 'declined'} the interview`,
        link_to:   JSON.stringify({ tab: 'Messages', conversation_id: activeId }),
      }).then(() => {});
    }
  };

  // ── Interviewer (employer/union) opens the Schedule Interview modal pre-filled with an
  //    existing interview's details, in "reschedule" mode rather than "create new" ──
  const openRescheduleModal = (iv) => {
    const when = new Date(iv.interview_at);
    const pad = n => String(n).padStart(2, '0');
    setInterviewForm({
      date: `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`,
      time: `${pad(when.getHours())}:${pad(when.getMinutes())}`,
      duration: String(iv.duration_minutes || 30),
      meetingType: iv.meeting_type || 'daily',
      meetingLink: iv.meeting_type !== 'daily' ? (iv.meeting_link || '') : '',
      notes: iv.notes || '',
    });
    setRescheduleTarget(iv);
    setShowInterviewModal(true);
  };

  // ── Interviewer directly changes the time of an existing interview -- resets status to
  //    'pending' since the previously-accepted time no longer applies, and resolves any
  //    outstanding reschedule request from the interviewee in the same update ──────────
  const handleRescheduleInterview = async () => {
    const { date, time, duration, meetingType, meetingLink, notes } = interviewForm;
    const isDaily = meetingType === 'daily';
    const iv = rescheduleTarget;
    if (!date || !time || (!isDaily && !meetingLink.trim()) || !iv || !activeId) return;
    setSendingInterview(true);

    const conv = conversations.find(c => c.id === activeId);
    if (!conv) { setSendingInterview(false); return; }

    let finalMeetingLink = isDaily ? iv.meeting_link : meetingLink.trim();
    if (isDaily) {
      try {
        const expiryTimestamp = Math.floor(new Date(`${date}T${time}:00`).getTime() / 1000) + (24 * 3600);
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch('/api/create-daily-room', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({ convId: activeId, interviewId: iv.id, expiryTimestamp }),
        });
        const room = await res.json();
        if (!res.ok) {
          alert(t('messaging.couldNotCreateVideoRoomPrefix') + (room.error || t('messaging.unknownError')));
          setSendingInterview(false);
          return;
        }
        finalMeetingLink = room.url;
      } catch (err) {
        alert(t('messaging.somethingWrongCreatingRoomPrefix') + err.message);
        setSendingInterview(false);
        return;
      }
    }

    const { data: updated, error: ivError } = await supabase
      .from('interviews')
      .update({
        interview_at: new Date(`${date}T${time}:00`).toISOString(),
        duration_minutes: Number(duration),
        meeting_type: meetingType || 'google_meet',
        meeting_link: finalMeetingLink,
        notes: notes.trim(),
        status: 'pending',
        accepted_at: null,
        previous_interview_at: iv.interview_at,
        rescheduled_at: new Date().toISOString(),
        reschedule_requested_at: null,
        reschedule_reason: null,
      })
      .eq('id', iv.id)
      .select()
      .single();

    if (ivError || !updated) {
      alert(t('messaging.couldNotRescheduleInterviewPrefix') + (ivError?.message || t('messaging.unknownError')));
      setSendingInterview(false);
      return;
    }
    setActiveInterview(updated);

    let formattedDate = date;
    try {
      formattedDate = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
      });
    } catch {}

    const content = `📅 Interview rescheduled to ${formattedDate} at ${formatTime12h(time)}.`;
    const { data: newMsg } = await supabase
      .from('messages')
      .insert({ conversation_id: activeId, sender_id: myId, sender_type: userType, content })
      .select()
      .single();
    await supabase.from('conversations')
      .update({ updated_at: new Date().toISOString(), worker_hidden_at: null, employer_hidden_at: null, union_hidden_at: null })
      .eq('id', activeId);
    if (newMsg) {
      setMessages(prev => {
        if (prev.find(m => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });
      setLastMessages(prev => ({ ...prev, [activeId]: newMsg }));
      (await getOrCreateChannel(supabase, `conversation:${activeId}`)).send({
        type: 'broadcast',
        event: 'new_message',
        payload: { message: newMsg },
      });
      const otherPartyId = userType === 'worker' ? conv.employer_id : conv.worker_id;
      (await getOrCreateChannel(supabase, `inbox:${otherPartyId}`)).send({
        type: 'broadcast',
        event: 'new_message_notification',
        payload: { conversation_id: activeId, message: newMsg },
      });
    }

    supabase.from('notifications').insert({
      user_id:   conv.worker_id,
      user_type: 'worker',
      type:      'interview_rescheduled',
      message:   `${senderName || 'An employer'} rescheduled the interview to ${formattedDate} at ${formatTime12h(time)}`,
      link_to:   JSON.stringify({ tab: 'Messages', conversation_id: activeId }),
    }).then(() => {});
    sendNotificationEmail(supabase, {
      recipientId:   conv.worker_id,
      recipientType: 'worker',
      type:          'interview_rescheduled',
      company_name:  senderName || 'An employer',
      date:          formattedDate,
      time,
    });

    setShowInterviewModal(false);
    setRescheduleTarget(null);
    setInterviewForm({ date: '', time: '', duration: '30', meetingType: 'daily', meetingLink: '', notes: '' });
    setSendingInterview(false);
    loadConvsRef.current();
  };

  // ── Interviewee (worker) submits a reschedule REQUEST with a reason, rather than
  //    changing the time directly -- the interviewer sees it and can act on it ──────
  const handleSubmitRequestChange = async () => {
    const iv = requestChangeTarget;
    if (!iv || !requestChangeReason.trim() || !activeId) return;
    setSubmittingRequestChange(true);

    const { data: updated, error } = await supabase
      .from('interviews')
      .update({ reschedule_requested_at: new Date().toISOString(), reschedule_reason: requestChangeReason.trim() })
      .eq('id', iv.id)
      .select()
      .single();

    if (error || !updated) {
      alert(t('messaging.couldNotSubmitRequestPrefix') + (error?.message || t('messaging.unknownError')));
      setSubmittingRequestChange(false);
      return;
    }
    setActiveInterview(updated);

    const conv = conversations.find(c => c.id === activeId);
    if (conv) {
      const content = `🔄 ${senderName || 'The candidate'} requested a different time: "${requestChangeReason.trim()}"`;
      const { data: newMsg } = await supabase
        .from('messages')
        .insert({ conversation_id: activeId, sender_id: myId, sender_type: userType, content })
        .select()
        .single();
      if (newMsg) {
        setMessages(prev => (prev.find(m => m.id === newMsg.id) ? prev : [...prev, newMsg]));
        setLastMessages(prev => ({ ...prev, [activeId]: newMsg }));
        (await getOrCreateChannel(supabase, `conversation:${activeId}`)).send({
          type: 'broadcast', event: 'new_message', payload: { message: newMsg },
        });
      }
      const otherId   = conv.employer_id || conv.union_id;
      const otherType = conv.employer_id ? 'employer' : 'union';
      (await getOrCreateChannel(supabase, `inbox:${otherId}`)).send({
        type: 'broadcast', event: 'new_message_notification', payload: { conversation_id: activeId, message: newMsg },
      });
      supabase.from('notifications').insert({
        user_id:   otherId,
        user_type: otherType,
        type:      'interview_reschedule_requested',
        message:   `${senderName || 'The candidate'} requested a different interview time: "${requestChangeReason.trim()}"`,
        link_to:   JSON.stringify({ tab: 'Messages', conversation_id: activeId }),
      }).then(() => {});
    }

    setRequestChangeTarget(null);
    setRequestChangeReason('');
    setSubmittingRequestChange(false);
    loadConvsRef.current();
  };

  // ── Interviewer declines a pending reschedule request, leaving the original time intact ──
  const handleDeclineRescheduleRequest = async (iv) => {
    setDecliningRequestId(iv.id);
    const { data: updated, error } = await supabase
      .from('interviews')
      .update({ reschedule_requested_at: null, reschedule_reason: null })
      .eq('id', iv.id)
      .select()
      .single();

    if (error || !updated) {
      alert(t('messaging.couldNotDeclineRequestPrefix') + (error?.message || t('messaging.unknownError')));
      setDecliningRequestId(null);
      return;
    }
    setActiveInterview(updated);

    const conv = conversations.find(c => c.id === activeId);
    if (conv) {
      const content = `❌ ${senderName || 'The interviewer'} declined the reschedule request — the original time stays.`;
      const { data: newMsg } = await supabase
        .from('messages')
        .insert({ conversation_id: activeId, sender_id: myId, sender_type: userType, content })
        .select()
        .single();
      if (newMsg) {
        setMessages(prev => (prev.find(m => m.id === newMsg.id) ? prev : [...prev, newMsg]));
        setLastMessages(prev => ({ ...prev, [activeId]: newMsg }));
        (await getOrCreateChannel(supabase, `conversation:${activeId}`)).send({
          type: 'broadcast', event: 'new_message', payload: { message: newMsg },
        });
        (await getOrCreateChannel(supabase, `inbox:${conv.worker_id}`)).send({
          type: 'broadcast', event: 'new_message_notification', payload: { conversation_id: activeId, message: newMsg },
        });
      }
      supabase.from('notifications').insert({
        user_id:   conv.worker_id,
        user_type: 'worker',
        type:      'interview_reschedule_declined',
        message:   `${senderName || 'The interviewer'} declined your reschedule request — the original time stays`,
        link_to:   JSON.stringify({ tab: 'Messages', conversation_id: activeId }),
      }).then(() => {});
    }

    setDecliningRequestId(null);
    loadConvsRef.current();
  };

  // ── Open a scheduled interview's meeting directly ───────────
  // Daily calls pass just the interviewId now -- InterviewPage looks up the job,
  // candidate, and meeting link itself so the panels there always have real data.
  const openInterviewMeeting = (interview) => {
    if (!interview?.meeting_link) return;
    if (interview.meeting_type === 'daily') {
      window.open(`/interview?interviewId=${interview.id}`, '_blank');
    } else {
      window.open(interview.meeting_link, '_blank', 'noopener,noreferrer');
    }
  };

  // ── Join video interview from interview card ───────────────
  const joinVideoInterview = async (details, msgId) => {
    console.log('[Video] joinVideoInterview called with:', details, msgId);
    const expiryTimestamp = (() => {
      try {
        const interviewMs = new Date(details.date + 'T' + details.time + ':00').getTime();
        return Math.floor(interviewMs / 1000) + (24 * 3600);
      } catch {
        return Math.floor(Date.now() / 1000) + (24 * 3600);
      }
    })();
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/create-daily-room', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ convId: activeId, msgId, expiryTimestamp }),
      });
      console.log('[Video] API response status:', res.status);
      const room = await res.json();
      console.log('[Video] API response data:', room);
      if (!res.ok) {
        alert(t('messaging.couldNotStartVideoCallPrefix') + (room.error || t('messaging.unknownError')));
        return;
      }
      const companyName = userType === 'worker'
        ? (activeConv?.otherPartyName || '')
        : (senderName || '');
      const url = `/interview?roomUrl=${encodeURIComponent(room.url)}&jobTitle=${encodeURIComponent('')}&companyName=${encodeURIComponent(companyName)}`;
      window.open(url, '_blank');
    } catch (err) {
      console.error('[Video] joinVideoInterview error:', err);
      alert(t('messaging.somethingWrongStartingCallPrefix') + err.message);
    }
  };

  const handleKeyDown = e => {
    if (isMobile) return; // let Enter behave normally (newline), only the Send button sends on mobile
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSelectConv = convId => {
    setActiveId(convId);
    setMobileView('thread');
    pendingUnreadConvIds.delete(convId);
    // Optimistically mark the last message as read so the unread indicator clears immediately
    setConversations(prev => prev.map(c => {
      if (c.id !== convId || !c.lastMessage || c.lastMessage.sender_type === userType) return c;
      return { ...c, lastMessage: { ...c.lastMessage, read_at: new Date().toISOString() } };
    }));
  };

  // ── Delete a message (soft delete, sender only) ─────────────
  const handleDeleteMessage = async (msgId) => {
    const deletedAt = new Date().toISOString();
    await supabase.from('messages').update({ deleted_at: deletedAt }).eq('id', msgId);
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, deleted_at: deletedAt } : m));
    setLastMessages(prev => {
      const lm = prev[activeId];
      return lm && lm.id === msgId ? { ...prev, [activeId]: { ...lm, deleted_at: deletedAt } } : prev;
    });
    setConversations(prev => prev.map(c =>
      c.id === activeId && c.lastMessage?.id === msgId
        ? { ...c, lastMessage: { ...c.lastMessage, deleted_at: deletedAt } }
        : c
    ));
    (await getOrCreateChannel(supabase, `conversation:${activeId}`)).send({
      type: 'broadcast',
      event: 'message_deleted',
      payload: { message_id: msgId, deleted_at: deletedAt },
    });
    setMsgConfirmDeleteId(null);
  };

  // ── Hide a conversation from this user's inbox ──────────────
  const handleHideConversation = async (convId) => {
    const hiddenCol = HIDDEN_COL[userType] || 'employer_hidden_at';
    await supabase.from('conversations').update({ [hiddenCol]: new Date().toISOString() }).eq('id', convId);
    setConversations(prev => prev.filter(c => c.id !== convId));
    if (activeId === convId) {
      setActiveId(null);
      setMobileView('list');
    }
    setConvConfirmDeleteId(null);
  };

  // ── Build message rows with date separators ────────────────
  const buildMessageRows = () => {
    const rows = [];
    let lastLabel = null;
    messages.forEach(msg => {
      const label = formatDateLabel(msg.created_at);
      if (label !== lastLabel) {
        rows.push({ type: 'sep', label, key: `sep-${msg.id}` });
        lastLabel = label;
      }
      rows.push({ type: 'msg', msg, key: msg.id });
    });
    return rows;
  };

  const activeConv = conversations.find(c => c.id === activeId);
  if (activeConv) lastActiveConvRef.current = activeConv;
  // Header identity: the live conversation, or (organization side, thread just lost) the last one seen.
  const headerConv = activeConv || (threadGone && lastActiveConvRef.current?.id === activeId ? lastActiveConvRef.current : null);
  // Organization threads have no file attachments: the message-attachments storage policy
  // isn't confirmed for organization staff.
  const orgThread = userType === 'organization' || !!activeConv?.organization_id;

  // Vocation Works Support (see useSupportInbox): a normal-looking row once the account's support
  // conversation has a message, placed by its latest message among the conversations (their own
  // order is unchanged), and a "Message support" link at the end of the list. Both open the
  // dashboard's support chat.
  const supportRowShown = !!support?.lastMessage;
  const convTime = c => new Date((lastMessages[c.id] ?? c.lastMessage)?.created_at || c.updated_at || 0).getTime();
  const supportIndex = !supportRowShown ? -1 : (() => {
    const at = new Date(support.lastMessage.created_at).getTime();
    const i = conversations.findIndex(c => convTime(c) < at);
    return i === -1 ? conversations.length : i;
  })();
  const withSupportRow = rows => (supportIndex < 0 ? rows : [...rows.slice(0, supportIndex), renderSupportRow(), ...rows.slice(supportIndex)]);
  const renderSupportRow = () => {
    const lm = support.lastMessage;
    const isUnread = support.hasUnread;
    return (
      <div
        key="vw-support"
        data-support-row
        role="button"
        tabIndex={0}
        onClick={support.open}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); support.open(); } }}
        style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '12px 16px', cursor: 'pointer',
          backgroundColor: isUnread ? 'rgba(255,255,255,0.05)' : 'transparent',
          borderLeft: '3px solid transparent',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          backgroundColor: '#132233', border: '1px solid rgba(255,255,255,0.12)', boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <VocationWorksLogo variant="reversed" layout="mark" size={22} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
            <span style={{ fontSize: '13px', fontWeight: isUnread ? 700 : 600, color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t('support.vocationWorksSupport')}
            </span>
            <span style={{ fontSize: '11px', color: isUnread ? AMBER : 'rgba(255,255,255,0.3)', flexShrink: 0, marginLeft: '8px' }}>
              {timeAgo(lm.created_at)}
            </span>
          </div>
          <div style={{ fontSize: '12px', color: isUnread ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.38)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: isUnread ? 600 : 400 }}>
            {(lm.sender_type === 'admin' ? '' : t('messaging.youPrefix')) + (lm.content || '')}
          </div>
        </div>
        {isUnread && (
          <div data-unread-dot style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: AMBER, flexShrink: 0 }} />
        )}
        {/* Same width as the other rows' conversation menu, so the dots line up. No menu here:
            the support conversation can't be deleted from the list. */}
        <div aria-hidden style={{ width: '26px', flexShrink: 0 }} />
      </div>
    );
  };
  const supportLink = support ? (
    <p data-support-link style={{ padding: '8px 16px 16px', margin: 0, fontSize: '14px', lineHeight: 1.45, color: 'rgba(255,255,255,0.55)', textAlign: 'center' }}>
      {t('messaging.supportLinkPrompt')}{' '}
      <button
        type="button"
        onClick={support.open}
        disabled={support.contacting}
        style={{
          background: 'none', border: 'none', padding: '0 2px', minHeight: '44px', verticalAlign: 'middle',
          color: AMBER, fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 600, textDecoration: 'underline',
          cursor: support.contacting ? 'default' : 'pointer', opacity: support.contacting ? 0.7 : 1,
        }}
      >
        {support.contacting ? t('support.startingEllipsis') : t('messaging.supportLinkAction')}
      </button>
    </p>
  ) : null;

  // ── Panel visibility ───────────────────────────────────────
  const showList   = !isMobile || mobileView === 'list';
  const showThread = !isMobile || mobileView === 'thread';

  // ── Shared styles ──────────────────────────────────────────
  const panelHeader = {
    height: '52px', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '10px',
    backgroundColor: NAVY, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
  };

  // ── Modal field style ──────────────────────────────────────
  const modalField = {
    width: '100%', boxSizing: 'border-box',
    backgroundColor: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px', padding: '9px 12px',
    color: WHITE, fontFamily: FONT_BODY, fontSize: '14px',
    outline: 'none',
  };

  console.table(messages.map(m => ({ id: m.id.slice(0, 8), sender: m.sender_type, content: m.content, created: m.created_at })));

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: FONT_BODY, backgroundColor: NAVY, overflow: 'hidden' }}>

      {/* ── Left: conversation list ── */}
      {showList && (
        <div style={{
          width: isMobile ? '100%' : '300px', flexShrink: 0,
          borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.08)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={panelHeader}>
            <span style={{ fontFamily: FONT_HEAD, fontSize: '1.6rem', color: WHITE, letterSpacing: '0.06em' }}>
              {t('messaging.panelTitle')}
            </span>
            {(() => {
              const unreadCount = conversations.filter(c => {
                const lm = c.lastMessage;
                return lm && lm.sender_type !== userType && lm.read_at === null;
              }).length + (support?.hasUnread ? 1 : 0);
              return unreadCount > 0 ? (
                <span style={{
                  backgroundColor: AMBER, color: NAVY,
                  borderRadius: '999px', fontSize: '11px', fontWeight: 700,
                  padding: '2px 7px', lineHeight: 1.4,
                }}>
                  {unreadCount}
                </span>
              ) : null;
            })()}
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loadingConvs ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '13px' }}>
                {t('common.loading')}
              </div>
            ) : conversations.length === 0 && !supportRowShown ? (
              <>
              <div style={{ padding: '32px 20px', textAlign: 'center', color: emptyListText ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)', fontSize: emptyListText ? '14px' : '13px', lineHeight: 1.6 }}>
                {emptyListText || t('messaging.noConversationsYet')}
              </div>
              {supportLink}
              </>
            ) : (
              <div key={conversationListVersion}>
              {withSupportRow(conversations.map(conv => {
                const isActive  = conv.id === activeId;
                const lm        = lastMessages[conv.id] ?? conv.lastMessage;
                const isUnread  = lm && lm.sender_type !== userType && lm.read_at === null;
                const lastContent = lm?.content || '';
                const lastPreview = lm?.deleted_at
                  ? t('messaging.deletedMessage')
                  : lastContent.startsWith('INTERVIEW:')
                    ? t('messaging.interviewScheduledLabel')
                    : lastContent.startsWith(OFFER_PREFIX)
                    ? (userType === 'worker' ? t('messaging.offerCardTitle') : t('messaging.offerSentTitle'))
                    : lm?.attachment_url
                      ? `📎 ${lm.attachment_name || t('messaging.attachmentFallback')}`
                      : lastContent;
                return (
                  <div
                    key={conv.id}
                    data-conversation-id={conv.id}
                    onClick={() => handleSelectConv(conv.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '12px 16px', cursor: 'pointer',
                      backgroundColor: isActive
                        ? 'rgba(245,166,35,0.07)'
                        : isUnread ? 'rgba(255,255,255,0.05)' : 'transparent',
                      borderLeft: `3px solid ${isActive ? AMBER : 'transparent'}`,
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      position: 'relative',
                    }}
                  >
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                      backgroundColor: AMBER, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '14px', fontWeight: 700, color: NAVY, overflow: 'hidden',
                    }}>
                      {conv.otherPartyLogoUrl
                        ? <ZoomableImg src={conv.otherPartyLogoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        : conv.otherPartyInitials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
                        <span style={{ fontSize: '13px', fontWeight: isUnread ? 700 : 600, color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {conv.otherPartyName}
                        </span>
                        {lm && (
                          <span style={{ fontSize: '11px', color: isUnread ? AMBER : 'rgba(255,255,255,0.3)', flexShrink: 0, marginLeft: '8px' }}>
                            {timeAgo(lm.created_at)}
                          </span>
                        )}
                      </div>
                      {userType === 'worker' && conv.organization_id && (
                        <div data-support-org-label style={{ fontSize: '14px', color: AMBER, marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t('messaging.supportOrganizationLabel')}
                        </div>
                      )}
                      <div style={{ fontSize: '12px', color: isUnread ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.38)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: isUnread ? 600 : 400 }}>
                        {lm && !lm.deleted_at && lastContent.startsWith('INTERVIEW:') && (
                          <LineIcon name="calendar" size={16} style={{ display: 'inline-block', verticalAlign: '-3px', marginRight: '4px' }} />
                        )}
                        {lm
                          ? (lm.sender_id === myId ? t('messaging.youPrefix') : '') + lastPreview
                          : t('messaging.noMessagesYetShort')}
                      </div>
                    </div>
                    {isUnread && (
                      <div data-unread-dot style={{
                        width: 8, height: 8, borderRadius: '50%',
                        backgroundColor: AMBER, flexShrink: 0,
                      }} />
                    )}
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setConvMenuOpenId(prev => prev === conv.id ? null : conv.id); }}
                        style={{
                          background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)',
                          fontSize: '18px', cursor: 'pointer', padding: '2px 6px', lineHeight: 1,
                        }}
                      >
                        ⋯
                      </button>
                      {convMenuOpenId === conv.id && (
                        <div
                          onClick={e => e.stopPropagation()}
                          style={{
                            position: 'absolute', top: '26px', right: 0, zIndex: 20,
                            backgroundColor: '#132738', border: '1px solid rgba(255,255,255,0.12)',
                            borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
                            minWidth: '170px', overflow: 'hidden',
                          }}
                        >
                          <button
                            onClick={() => { setConvMenuOpenId(null); setConvConfirmDeleteId(conv.id); }}
                            style={{
                              width: '100%', textAlign: 'left', padding: '10px 14px',
                              background: 'none', border: 'none', color: '#FF6B6B',
                              fontSize: '13px', fontFamily: FONT_BODY, cursor: 'pointer',
                            }}
                          >
                            {t('messaging.deleteConversation')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }))}
              {supportLink}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Right: thread ── */}
      {showThread && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {!activeId ? (
            // Empty state
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px' }}>
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <p style={{ color: 'rgba(255,255,255,0.22)', fontSize: '14px' }}>{t('messaging.selectAConversation')}</p>
            </div>
          ) : (
            <div
              data-testid="thread-dropzone"
              style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isDraggingFile && (
                <div style={{
                  position: 'absolute', inset: 0, zIndex: 50,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px',
                  backgroundColor: 'rgba(13,27,42,0.88)',
                  border: `2px dashed ${AMBER}`, borderRadius: '8px', margin: '6px',
                  pointerEvents: 'none',
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={AMBER} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                  <p style={{ color: AMBER, fontSize: '14px', fontWeight: 600, fontFamily: FONT_BODY, margin: 0 }}>
                    {t('messaging.dropFileToAttach')}
                  </p>
                </div>
              )}
              {/* Thread header */}
              <div style={panelHeader}>
                {isMobile && (
                  <button
                    onClick={() => setMobileView('list')}
                    style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: '4px 8px 4px 0', fontSize: '18px', lineHeight: 1, fontFamily: FONT_BODY }}
                  >
                    ←
                  </button>
                )}
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  backgroundColor: AMBER, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: 700, color: NAVY, overflow: 'hidden',
                }}>
                  {headerConv?.otherPartyLogoUrl
                    ? <ZoomableImg src={headerConv.otherPartyLogoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    : headerConv?.otherPartyInitials}
                </div>
                <div
                  onClick={() => {
                    if (userType === 'worker' && activeConv?.employer_id) {
                      navigate(`/company/${activeConv.employer_id}`);
                    } else if ((userType === 'employer' || userType === 'union') && activeConv?.worker_id) {
                      navigate(`/worker/profile/${activeConv.worker_id}`);
                    }
                  }}
                  style={{ flex: 1, cursor: activeConv?.organization_id ? 'default' : 'pointer', minWidth: 0 }}
                >
                  <div style={{ fontSize: '14px', fontWeight: 600, color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {headerConv?.otherPartyName}
                  </div>
                  {userType === 'union' && activeConv?.otherPartyTrade && (
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '1px' }}>
                      {activeConv.otherPartyTrade}
                    </div>
                  )}
                  {userType === 'worker' && activeConv?.organization_id && (
                    <div style={{ fontSize: '14px', color: AMBER, marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t('messaging.supportOrganizationLabel')}
                    </div>
                  )}
                </div>
                {(userType === 'employer' || userType === 'union') && !messagingBlocked && (
                  <button
                    onClick={() => setShowInterviewModal(true)}
                    style={{
                      backgroundColor: AMBER, color: NAVY,
                      border: 'none', borderRadius: '20px',
                      padding: '5px 12px', fontSize: '12px', fontWeight: 700,
                      fontFamily: FONT_BODY, cursor: 'pointer', flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('messaging.scheduleInterview')}
                  </button>
                )}
              </div>

              {/* Hired banner */}
              {hiredApplication && (
                <div style={{ backgroundColor: 'rgba(16,185,129,0.1)', borderBottom: '1px solid rgba(16,185,129,0.25)', padding: '10px 16px', flexShrink: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#10B981' }}>
                    <LineIcon name="sparkle" size={16} />
                    {userType === 'employer'
                      ? t('messaging.hiredBannerEmployer', { name: activeConv?.otherPartyName })
                      : t('messaging.hiredBannerWorker', { name: activeConv?.otherPartyName })}
                  </span>
                </div>
              )}

              {/* Upcoming interview banner (from the interviews table, not a message) */}
              {activeInterview && (() => {
                const iv = activeInterview;
                const typeName = MEETING_TYPE_LABELS[iv.meeting_type] || 'Meeting';
                const when = new Date(iv.interview_at);
                const isPast = when < new Date();
                const shortDate = when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const timeLabel = when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                const isPendingForWorker = userType === 'worker' && iv.status === 'pending';
                const statusLabel = iv.status === 'accepted' ? t('messaging.statusConfirmed') : iv.status === 'declined' ? t('messaging.statusDeclined') : t('messaging.statusAwaitingResponse');
                const statusColor = iv.status === 'accepted' ? '#10B981' : iv.status === 'declined' ? '#F87171' : 'rgba(255,255,255,0.5)';
                const scheduledByName = iv.employer_id && iv.created_by && iv.created_by !== myId
                  ? employerStaffMap[iv.employer_id]?.[iv.created_by]?.display_name
                  : null;
                // The interviewer is always the employer or union side (the worker is always
                // the interviewee) -- they get the direct "Reschedule" power, matching who
                // already gets the "Schedule Interview" button in the header above.
                const isInterviewer = userType === 'employer' || userType === 'union';
                const wasRescheduled = !!iv.rescheduled_at;
                const hasPendingRequest = !!iv.reschedule_requested_at;
                const prevWhen = iv.previous_interview_at ? new Date(iv.previous_interview_at) : null;
                const canProposeChange = iv.status !== 'cancelled' && iv.status !== 'completed';
                const linkBtnStyle = {
                  background: 'none', border: 'none', padding: 0,
                  color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontFamily: FONT_BODY,
                  textDecoration: 'underline', cursor: 'pointer', flexShrink: 0,
                };
                return (
                  <div style={{ flexShrink: 0 }}>
                    <div style={{
                      borderLeft: `3px solid ${AMBER}`,
                      backgroundColor: '#1a2a3a',
                      padding: '10px 14px',
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      gap: '8px',
                    }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: AMBER, fontFamily: FONT_BODY }}>
                          <LineIcon name="calendar" size={16} />
                          {wasRescheduled ? t('messaging.interviewRescheduledLabel') : isPendingForWorker ? t('messaging.interviewInvitation') : isPast ? t('messaging.pastInterview') : t('messaging.upcomingInterview')}
                        </span>
                        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontFamily: FONT_BODY }}>
                          {shortDate} · {timeLabel} · {typeName}
                        </span>
                        {wasRescheduled && prevWhen && (
                          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', fontFamily: FONT_BODY, textDecoration: 'line-through' }}>
                            {t('messaging.previouslyScheduledForPrefix')}
                            {prevWhen.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {prevWhen.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </span>
                        )}
                        {scheduledByName && (
                          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontFamily: FONT_BODY }}>
                            {t('messaging.scheduledByPrefix')}{scheduledByName}
                          </span>
                        )}
                        {!isPendingForWorker && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 700, color: statusColor, fontFamily: FONT_BODY }}>
                            {iv.status === 'accepted' && <LineIcon name="check" size={16} />}
                            {statusLabel}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: '8px', flexShrink: 0 }}>
                        {isPendingForWorker ? (
                          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                            <button
                              onClick={() => handleInterviewResponse(iv.id, 'accepted')}
                              style={{
                                backgroundColor: AMBER, color: NAVY,
                                border: 'none', borderRadius: '16px',
                                padding: '6px 14px', fontSize: '12px', fontWeight: 700,
                                fontFamily: FONT_BODY, cursor: 'pointer',
                              }}
                            >
                              {t('messaging.accept')}
                            </button>
                            <button
                              onClick={() => handleInterviewResponse(iv.id, 'declined')}
                              style={{
                                backgroundColor: 'transparent', color: 'rgba(255,255,255,0.7)',
                                border: '1px solid rgba(255,255,255,0.2)', borderRadius: '16px',
                                padding: '6px 14px', fontSize: '12px', fontWeight: 700,
                                fontFamily: FONT_BODY, cursor: 'pointer',
                              }}
                            >
                              {t('messaging.decline')}
                            </button>
                          </div>
                        ) : iv.status === 'declined' ? null : (() => {
                          // A Daily call can be held before interview_at, so the transcript's state
                          // shows as soon as there is one. Before the scheduled time "Join" stays
                          // next to it (a dropped call can be rejoined); after it, the transcript
                          // state replaces "Join" (never a dead join link to a call that's over).
                          const isDaily = iv.meeting_type === 'daily';
                          const showJoin = !!iv.meeting_link && !(isDaily && isPast);
                          const showTranscript = isDaily && (isPast || iv.transcript_status === 'pending' || iv.transcript_status === 'ready');
                          return (
                            <>
                              {showJoin && (
                                <button
                                  onClick={() => openInterviewMeeting(iv)}
                                  style={{
                                    backgroundColor: AMBER, color: NAVY,
                                    border: 'none', borderRadius: '16px',
                                    padding: '6px 14px', fontSize: '12px', fontWeight: 700,
                                    fontFamily: FONT_BODY, cursor: 'pointer', flexShrink: 0,
                                  }}
                                >
                                  {isDaily ? t('messaging.joinVideoCall') : t('messaging.joinMeetingArrow')}
                                </button>
                              )}
                              {showTranscript && iv.transcript_status === 'ready' && (
                                <button
                                  data-view-transcript
                                  onClick={() => window.open(`/interview?interviewId=${iv.id}&view=transcript`, '_blank')}
                                  style={{
                                    backgroundColor: showJoin ? 'transparent' : AMBER,
                                    color: showJoin ? AMBER : NAVY,
                                    border: showJoin ? `1px solid ${AMBER}` : 'none', borderRadius: '16px',
                                    padding: showJoin ? '5px 13px' : '6px 14px', fontSize: '12px', fontWeight: 700,
                                    fontFamily: FONT_BODY, cursor: 'pointer', flexShrink: 0,
                                  }}
                                >
                                  {t('messaging.viewTranscript')}
                                </button>
                              )}
                              {showTranscript && iv.transcript_status === 'pending' && (
                                <span data-transcript-processing style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', fontFamily: FONT_BODY, flexShrink: 0 }}>
                                  {t('messaging.transcriptProcessing')}
                                </span>
                              )}
                            </>
                          );
                        })()}

                        {/* Reschedule (interviewer's direct power) / Request a different time
                            (interviewee's request-only path) -- this is also what gives a
                            declined interview a real next step instead of a dead end. */}
                        {isInterviewer && canProposeChange && !hasPendingRequest && (
                          <button onClick={() => openRescheduleModal(iv)} style={linkBtnStyle}>
                            {t('messaging.reschedule')}
                          </button>
                        )}
                        {!isInterviewer && !isPendingForWorker && canProposeChange && !hasPendingRequest && (
                          <button onClick={() => setRequestChangeTarget(iv)} style={linkBtnStyle}>
                            {t('messaging.requestDifferentTime')}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Pending reschedule request -- the interviewer sees the reason and can
                        either propose a new time directly or decline the request outright. */}
                    {hasPendingRequest && (
                      <div style={{
                        borderLeft: `3px solid ${AMBER}`,
                        backgroundColor: '#22344a',
                        padding: '10px 14px',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        gap: '8px',
                      }}>
                        {isInterviewer ? (
                          <>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontFamily: FONT_BODY }}>
                              <LineIcon name="refresh" size={16} style={{ marginTop: '1px' }} />
                              {t('messaging.requestedNewTimePrefix')}"{iv.reschedule_reason}"
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                              <button
                                onClick={() => openRescheduleModal(iv)}
                                style={{
                                  backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '16px',
                                  padding: '6px 14px', fontSize: '12px', fontWeight: 700, fontFamily: FONT_BODY, cursor: 'pointer',
                                }}
                              >
                                {t('messaging.proposeNewTime')}
                              </button>
                              <button
                                onClick={() => handleDeclineRescheduleRequest(iv)}
                                disabled={decliningRequestId === iv.id}
                                style={{
                                  backgroundColor: 'transparent', color: 'rgba(255,255,255,0.7)',
                                  border: '1px solid rgba(255,255,255,0.2)', borderRadius: '16px',
                                  padding: '6px 14px', fontSize: '12px', fontWeight: 700, fontFamily: FONT_BODY,
                                  cursor: decliningRequestId === iv.id ? 'default' : 'pointer',
                                }}
                              >
                                {decliningRequestId === iv.id ? t('messaging.decliningEllipsis') : t('messaging.declineRequest')}
                              </button>
                            </div>
                          </>
                        ) : (
                          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY, fontStyle: 'italic' }}>
                            {t('messaging.rescheduleRequestSent')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column' }}>
                {loadingMsgs ? (
                  <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px', marginTop: '24px' }}>
                    {t('messaging.loadingMessages')}
                  </div>
                ) : threadGone ? null : messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px', marginTop: '24px' }}>
                    {t('messaging.noMessagesYetLong')}
                  </div>
                ) : (
                  buildMessageRows().map(item => {
                    if (item.type === 'sep') {
                      return (
                        <div key={item.key} style={{ textAlign: 'center', margin: '16px 0 10px' }}>
                          <span style={{
                            fontSize: '11px', color: 'rgba(255,255,255,0.3)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            borderRadius: '999px', padding: '3px 12px',
                          }}>
                            {item.label}
                          </span>
                        </div>
                      );
                    }

                    const { msg } = item;
                    // Side-based (not individual-based), so a teammate's message still
                    // groups/aligns as "our side" -- matches the messages UPDATE policy,
                    // which already lets any employer_staff teammate act on any employer-
                    // side message, not just the literal sender.
                    const isMine = msg.sender_type === userType;
                    const isDeleted = !!msg.deleted_at;
                    const isInterview = typeof msg.content === 'string' && msg.content.startsWith('INTERVIEW:');
                    const interviewDetails = isInterview ? parseInterviewContent(msg.content) : null;
                    const offerDetails = typeof msg.content === 'string' && msg.content.startsWith(OFFER_PREFIX) ? parseOfferContent(msg.content) : null;
                    const hasAttachment = !!msg.attachment_url;
                    // Individual-based -- who exactly sent this, for the small attribution
                    // row shown on any employer-side message that isn't the viewer's own.
                    // Falls back to the shared company identity if this specific sender_id
                    // isn't in the roster (predates this feature -- legacy rows still carry
                    // the old shared employer_id as sender_id -- or the person has since left).
                    // Union attribution is teammate-facing only: a worker can't read union_staff,
                    // so labelling union messages for them would just repeat the union's own name.
                    const needsSenderAttribution = msg.sender_id !== myId
                      && (msg.sender_type === 'employer'
                        || (msg.sender_type === 'union' && userType === 'union')
                        || (msg.sender_type === 'organization' && userType === 'organization'));
                    const resolvedSender = !needsSenderAttribution ? null
                      : msg.sender_type === 'union'
                        ? unionStaffMap[activeConv?.union_id]?.[msg.sender_id]
                        : msg.sender_type === 'organization'
                          ? orgStaffMap[userId]?.[msg.sender_id]
                          : employerStaffMap[activeConv?.employer_id]?.[msg.sender_id];
                    const senderDisplay = !needsSenderAttribution ? null : resolvedSender?.display_name
                      ? { name: resolvedSender.display_name, avatarUrl: resolvedSender.avatar_url, initials: resolvedSender.display_name[0] }
                      : userType === 'worker'
                        ? { name: activeConv?.otherPartyName, avatarUrl: activeConv?.otherPartyLogoUrl, initials: activeConv?.otherPartyInitials }
                        : { name: t('messaging.teamMemberFallback'), avatarUrl: null, initials: '?' };
                    const showAction = isMine && !isDeleted && msgActionId === msg.id;
                    const rowHoverProps = isMine && !isDeleted
                      ? (isMobile
                          ? { onClick: (e) => { e.stopPropagation(); setMsgActionId(prev => prev === msg.id ? null : msg.id); } }
                          : { onMouseEnter: () => setMsgActionId(msg.id), onMouseLeave: () => setMsgActionId(prev => prev === msg.id ? null : prev) })
                      : {};

                    return (
                      <div key={item.key} style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: isMine ? 'flex-end' : 'flex-start',
                        alignSelf: isMine ? 'flex-end' : 'flex-start',
                        marginBottom: '6px',
                        maxWidth: '68%',
                      }}>
                        {senderDisplay && (
                          <div style={{ display: 'flex', flexDirection: isMine ? 'row-reverse' : 'row', alignItems: 'center', gap: '5px', marginBottom: '3px', padding: isMine ? '0 2px 0 0' : '0 0 0 2px' }}>
                            <div style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, overflow: 'hidden', backgroundColor: AMBER, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {senderDisplay.avatarUrl
                                ? <ZoomableImg src={senderDisplay.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                : <span style={{ fontSize: '8px', fontWeight: 700, color: NAVY }}>{senderDisplay.initials}</span>}
                            </div>
                            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{senderDisplay.name}</span>
                          </div>
                        )}
                        <div
                          {...rowHoverProps}
                          style={{ display: 'flex', flexDirection: isMine ? 'row-reverse' : 'row', alignItems: 'center', gap: '6px' }}
                        >
                          {isDeleted ? (
                            <div style={{
                              padding: '9px 13px',
                              backgroundColor: 'transparent',
                              border: '1px dashed rgba(255,255,255,0.15)',
                              color: 'rgba(255,255,255,0.35)', fontStyle: 'italic',
                              borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                              fontSize: '13.5px', lineHeight: 1.5,
                            }}>
                              {t('messaging.deletedMessage')}
                            </div>
                          ) : offerDetails ? (
                            <OfferCard details={offerDetails} isMine={isMine} userType={userType} onViewOffer={onViewOffer} />
                          ) : isInterview ? (
                            interviewDetails
                              ? <InterviewCard details={interviewDetails} isMine={isMine} onJoinVideo={() => joinVideoInterview(interviewDetails, msg.id)} />
                              : (
                                <div style={{
                                  padding: '10px 14px',
                                  borderLeft: `4px solid ${AMBER}`,
                                  backgroundColor: isMine ? '#1A3A5C' : 'rgba(255,255,255,0.08)',
                                  borderRadius: '0 12px 12px 0',
                                  display: 'flex', alignItems: 'center', gap: '6px',
                                  fontSize: '13px', color: AMBER, fontFamily: FONT_BODY,
                                }}>
                                  <LineIcon name="calendar" size={16} />
                                  {t('messaging.interviewScheduledLabel')}
                                </div>
                              )
                          ) : hasAttachment ? (
                            <div style={{
                              padding: '10px 13px',
                              backgroundColor: isMine ? '#1A3A5C' : 'rgba(255,255,255,0.1)',
                              color: WHITE,
                              borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                              display: 'flex', alignItems: 'center', gap: '8px',
                            }}>
                              <span style={{ color: AMBER, flexShrink: 0, display: 'flex' }}>
                                <AttachmentIcon type={msg.attachment_type} />
                              </span>
                              <span
                                title={msg.attachment_name}
                                style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}
                              >
                                {msg.attachment_name || t('messaging.attachmentFallback')}
                              </span>
                              <button
                                onClick={() => handleDownloadAttachment(msg)}
                                disabled={downloadingId === msg.id}
                                title={t('messaging.download')}
                                style={{
                                  background: 'none', border: 'none', color: AMBER,
                                  cursor: downloadingId === msg.id ? 'default' : 'pointer',
                                  fontSize: '14px', fontWeight: 700, flexShrink: 0, padding: 0,
                                  display: 'flex', alignItems: 'center',
                                }}
                              >
                                {downloadingId === msg.id ? '…' : (
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="7 10 12 15 17 10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                  </svg>
                                )}
                              </button>
                            </div>
                          ) : (
                            <div style={{
                              padding: '9px 13px',
                              backgroundColor: isMine ? '#1A3A5C' : 'rgba(255,255,255,0.1)',
                              color: WHITE,
                              borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                              fontSize: '13.5px', lineHeight: 1.5,
                              overflowWrap: 'break-word',
                            }}>
                              {msg.content}
                            </div>
                          )}
                          {showAction && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setMsgConfirmDeleteId(msg.id); setMsgActionId(null); }}
                              title={t('messaging.deleteMessageTitle')}
                              style={{
                                background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%',
                                width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: 'rgba(255,255,255,0.5)', cursor: 'pointer', flexShrink: 0, padding: 0,
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              </svg>
                            </button>
                          )}
                        </div>
                        {isDeleted && (
                          <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.25)', marginTop: '2px' }}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                          </span>
                        )}
                        {isMine && msg.read_at && !isDeleted && (
                          <span style={{ fontSize: '10px', color: AMBER, marginTop: '2px', paddingRight: '2px' }}>
                            {t('messaging.read')}
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input -- or, for an employer whose subscription has lapsed, a blocked-state
                  message in its place. This is a friendly pre-check only; the messages
                  INSERT RLS policy is what actually stops the write either way. */}
              {threadGone ? (
                <div role="status" data-thread-gone style={{
                  padding: '14px 16px', borderTop: '1px solid rgba(255,255,255,0.08)',
                  backgroundColor: NAVY, flexShrink: 0,
                }}>
                  <span style={{ fontSize: '14px', color: '#F87171', fontWeight: 500 }}>
                    {t('messaging.clientNoLongerConnected')}
                  </span>
                </div>
              ) : messagingBlocked ? (
                <div style={{
                  padding: '14px 16px', borderTop: '1px solid rgba(255,255,255,0.08)',
                  backgroundColor: NAVY, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
                }}>
                  <span style={{ fontSize: '13px', color: '#F87171', fontWeight: 500 }}>
                    {t('messaging.subscriptionLapsedMessage')}
                  </span>
                  <span
                    onClick={() => navigate('/employer/pricing')}
                    style={{ fontSize: '13px', color: AMBER, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {t('messaging.subscriptionLapsedLink')}
                  </span>
                </div>
              ) : (
              <div style={{
                padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)',
                display: 'flex', alignItems: 'flex-end', gap: '10px',
                backgroundColor: NAVY, flexShrink: 0,
              }}>
                {!orgThread && (<>
                <input
                  ref={attachInputRef}
                  type="file"
                  accept={ATTACHMENT_ACCEPT}
                  style={{ display: 'none' }}
                  onChange={handleAttachmentSelected}
                />
                <button
                  onClick={() => attachInputRef.current?.click()}
                  disabled={attachUploading}
                  title={t('messaging.attachAFile')}
                  style={{
                    width: '36px', height: '36px', borderRadius: '50%', border: 'none', flexShrink: 0,
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: attachUploading ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.7)',
                    cursor: attachUploading ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {attachUploading ? (
                    <span style={{ fontSize: '10px' }}>…</span>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                    </svg>
                  )}
                </button>
                </>)}
                <textarea
                  ref={textareaRef}
                  value={text}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onCompositionStart={() => setIsComposing(true)}
                  onCompositionEnd={() => setIsComposing(false)}
                  placeholder={t('messaging.typeAMessage')}
                  rows={1}
                  style={{
                    flex: 1, resize: 'none',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '20px', padding: '9px 14px',
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    color: WHITE, fontFamily: FONT_BODY, fontSize: '16px',
                    lineHeight: 1.5, outline: 'none', overflowY: 'hidden',
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={!text.trim() || sending}
                  style={{
                    width: '36px', height: '36px', borderRadius: '50%', border: 'none', flexShrink: 0,
                    backgroundColor: text.trim() && !sending ? AMBER : 'rgba(255,255,255,0.12)',
                    color: text.trim() && !sending ? NAVY : 'rgba(255,255,255,0.35)',
                    cursor: text.trim() && !sending ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background-color 0.15s',
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                </button>
              </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Schedule Interview Modal ── */}
      {showInterviewModal && (
        <div
          onClick={e => { if (e.target === e.currentTarget) { setShowInterviewModal(false); setRescheduleTarget(null); } }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            backgroundColor: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div style={{
            backgroundColor: NAVY, borderRadius: '14px',
            border: '1px solid rgba(255,255,255,0.12)',
            padding: '28px 24px', width: '100%', maxWidth: '440px',
            display: 'flex', flexDirection: 'column', gap: '18px',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <h2 style={{ fontFamily: FONT_HEAD, fontSize: '2rem', color: WHITE, letterSpacing: '0.06em', margin: 0 }}>
              {rescheduleTarget ? t('messaging.rescheduleInterview') : t('messaging.scheduleInterview')}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY }}>
                {t('messaging.interviewDateLabel')}
              </label>
              <input
                type="date"
                value={interviewForm.date}
                onChange={e => setInterviewForm(f => ({ ...f, date: e.target.value }))}
                style={{ ...modalField, colorScheme: 'dark' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY }}>
                {t('messaging.interviewTimeLabel')}
              </label>
              <select
                value={interviewForm.time}
                onChange={e => setInterviewForm(f => ({ ...f, time: e.target.value }))}
                style={{ ...modalField }}
              >
                <option value="">{t('messaging.selectATime')}</option>
                {TIME_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY }}>
                {t('messaging.durationFieldLabel')}
              </label>
              <select
                value={interviewForm.duration}
                onChange={e => setInterviewForm(f => ({ ...f, duration: e.target.value }))}
                style={{ ...modalField }}
              >
                <option value="30">{t('messaging.duration30')}</option>
                <option value="45">{t('messaging.duration45')}</option>
                <option value="60">{t('messaging.duration60')}</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY }}>
                {t('messaging.meetingTypeLabel')}
              </label>
              <select
                value={interviewForm.meetingType}
                onChange={e => setInterviewForm(f => ({ ...f, meetingType: e.target.value, meetingLink: '' }))}
                style={{ ...modalField }}
              >
                <option value="google_meet">Google Meet</option>
                <option value="zoom">Zoom</option>
                <option value="teams">Microsoft Teams</option>
                <option value="daily">Vocation Works Video</option>
              </select>
            </div>

            {interviewForm.meetingType !== 'daily' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY }}>
                  {t('messaging.meetingLinkFieldLabel')}
                </label>
                <input
                  type="url"
                  value={interviewForm.meetingLink}
                  onChange={e => setInterviewForm(f => ({ ...f, meetingLink: e.target.value }))}
                  placeholder={
                    interviewForm.meetingType === 'zoom' ? t('messaging.pasteLinkZoom') :
                    interviewForm.meetingType === 'teams' ? t('messaging.pasteLinkTeams') :
                    t('messaging.pasteLinkGoogleMeet')
                  }
                  style={{ ...modalField }}
                />
              </div>
            )}

            {interviewForm.meetingType === 'daily' && (
              <div style={{
                backgroundColor: 'rgba(245,166,35,0.08)',
                border: '1px solid rgba(245,166,35,0.25)',
                borderRadius: '8px', padding: '12px 14px',
              }}>
                <p style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontFamily: FONT_BODY, margin: 0, lineHeight: 1.5 }}>
                  <LineIcon name="video" size={16} color="#3B82F6" style={{ marginTop: '2px' }} />
                  {t('messaging.videoRoomAutoBanner')}
                </p>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY }}>
                {t('messaging.notesFieldLabel')} <span style={{ color: 'rgba(255,255,255,0.3)' }}>{t('worker.signup.step4.optional')}</span>
              </label>
              <textarea
                value={interviewForm.notes}
                onChange={e => setInterviewForm(f => ({ ...f, notes: e.target.value }))}
                placeholder={t('messaging.notesPlaceholder')}
                rows={3}
                style={{ ...modalField, resize: 'vertical', lineHeight: 1.5 }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
              <button
                onClick={rescheduleTarget ? handleRescheduleInterview : handleSendInterviewInvite}
                disabled={!(interviewForm.date && interviewForm.time && (interviewForm.meetingType === 'daily' || interviewForm.meetingLink.trim())) || sendingInterview}
                style={{
                  flex: 1, padding: '11px', borderRadius: '8px', border: 'none',
                  backgroundColor: (!(interviewForm.date && interviewForm.time && (interviewForm.meetingType === 'daily' || interviewForm.meetingLink.trim())) || sendingInterview)
                    ? 'rgba(245,166,35,0.4)' : AMBER,
                  color: NAVY, fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 700,
                  cursor: (!(interviewForm.date && interviewForm.time && (interviewForm.meetingType === 'daily' || interviewForm.meetingLink.trim())) || sendingInterview)
                    ? 'not-allowed' : 'pointer',
                }}
              >
                {sendingInterview
                  ? t('messaging.sendingEllipsis')
                  : rescheduleTarget ? t('messaging.saveNewTime') : t('messaging.sendInterviewInvite')}
              </button>
              <button
                onClick={() => { setShowInterviewModal(false); setRescheduleTarget(null); }}
                style={{
                  padding: '11px 18px', borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  backgroundColor: 'transparent', color: 'rgba(255,255,255,0.7)',
                  fontFamily: FONT_BODY, fontSize: '14px', cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Request a Different Time Modal (interviewee's reschedule request) ── */}
      {requestChangeTarget && (
        <div
          onClick={e => { if (e.target === e.currentTarget) { setRequestChangeTarget(null); setRequestChangeReason(''); } }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            backgroundColor: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div style={{
            backgroundColor: NAVY, borderRadius: '14px',
            border: '1px solid rgba(255,255,255,0.12)',
            padding: '28px 24px', width: '100%', maxWidth: '400px',
            display: 'flex', flexDirection: 'column', gap: '18px',
          }}>
            <h2 style={{ fontFamily: FONT_HEAD, fontSize: '1.6rem', color: WHITE, letterSpacing: '0.06em', margin: 0 }}>
              {t('messaging.requestDifferentTime')}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY }}>
                {t('messaging.reasonLabel')}
              </label>
              <textarea
                value={requestChangeReason}
                onChange={e => setRequestChangeReason(e.target.value)}
                placeholder={t('messaging.reasonPlaceholder')}
                rows={3}
                autoFocus
                style={{ ...modalField, resize: 'vertical', lineHeight: 1.5 }}
              />
              {!requestChangeReason.trim() && (
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', fontFamily: FONT_BODY }}>
                  {t('messaging.requestReasonRequiredError')}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleSubmitRequestChange}
                disabled={!requestChangeReason.trim() || submittingRequestChange}
                style={{
                  flex: 1, padding: '11px', borderRadius: '8px', border: 'none',
                  backgroundColor: (!requestChangeReason.trim() || submittingRequestChange) ? 'rgba(245,166,35,0.4)' : AMBER,
                  color: NAVY, fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 700,
                  cursor: (!requestChangeReason.trim() || submittingRequestChange) ? 'not-allowed' : 'pointer',
                }}
              >
                {submittingRequestChange ? t('messaging.sendingEllipsis') : t('messaging.sendRequest')}
              </button>
              <button
                onClick={() => { setRequestChangeTarget(null); setRequestChangeReason(''); }}
                style={{
                  padding: '11px 18px', borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  backgroundColor: 'transparent', color: 'rgba(255,255,255,0.7)',
                  fontFamily: FONT_BODY, fontSize: '14px', cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Message Confirm ── */}
      {msgConfirmDeleteId && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setMsgConfirmDeleteId(null); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            backgroundColor: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div style={{
            backgroundColor: NAVY, borderRadius: '14px',
            border: '1px solid rgba(255,255,255,0.12)',
            padding: '24px', width: '100%', maxWidth: '340px',
            display: 'flex', flexDirection: 'column', gap: '16px',
          }}>
            <p style={{ fontSize: '14px', color: WHITE, fontFamily: FONT_BODY, lineHeight: 1.5, margin: 0 }}>
              {t('messaging.deleteMessageConfirm')}
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => handleDeleteMessage(msgConfirmDeleteId)}
                style={{
                  flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                  backgroundColor: '#FF6B6B', color: WHITE,
                  fontFamily: FONT_BODY, fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                }}
              >
                {t('messaging.delete')}
              </button>
              <button
                onClick={() => setMsgConfirmDeleteId(null)}
                style={{
                  padding: '10px 16px', borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  backgroundColor: 'transparent', color: 'rgba(255,255,255,0.7)',
                  fontFamily: FONT_BODY, fontSize: '13px', cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Conversation Confirm ── */}
      {convConfirmDeleteId && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setConvConfirmDeleteId(null); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            backgroundColor: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div style={{
            backgroundColor: NAVY, borderRadius: '14px',
            border: '1px solid rgba(255,255,255,0.12)',
            padding: '24px', width: '100%', maxWidth: '360px',
            display: 'flex', flexDirection: 'column', gap: '16px',
          }}>
            <p style={{ fontSize: '14px', color: WHITE, fontFamily: FONT_BODY, lineHeight: 1.5, margin: 0 }}>
              {t('messaging.removeConversationConfirm')}
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => handleHideConversation(convConfirmDeleteId)}
                style={{
                  flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                  backgroundColor: '#FF6B6B', color: WHITE,
                  fontFamily: FONT_BODY, fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                }}
              >
                {t('messaging.remove')}
              </button>
              <button
                onClick={() => setConvConfirmDeleteId(null)}
                style={{
                  padding: '10px 16px', borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  backgroundColor: 'transparent', color: 'rgba(255,255,255,0.7)',
                  fontFamily: FONT_BODY, fontSize: '13px', cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
